import { ApiConfig } from "../apiConfig/apiConfig";
import { ApiCallDelete, ApiCallGet, ApiCallGetVerifyRegistration, ApiCallPost, ApiCallPut } from "../apiConfig/apiCall";
import { buildSpotMeActivityQuery } from "./spotMeActivityQuery";
import {
  buildOptionsPnlAnalysisDetailsQuery,
  buildOptionsPnlAnalysisQuery,
} from "./optionsPnlQuery";
import {
  buildSpotPnlAnalysisDetailsQuery,
  buildSpotPnlAnalysisQuery,
} from "./spotPnlQuery";
import { ConsoleLogs } from "../../utils/ConsoleLogs";
import { normalizeNicknameForApi } from "../../utils/nicknameUtils";
import { deviceAuthHeaders } from "../../utils/deviceId";

const TAG = "AuthService";

/** `Authorization: Bearer <access_token>` + `X-Device-Id` for secured routes. */
function bearerAuthHeaders() {
  let raw = null;
  try {
    raw = localStorage.getItem("token") || sessionStorage.getItem("token");
  } catch {
    raw = null;
  }
  const headers = deviceAuthHeaders();
  if (!raw) return headers;
  const token = String(raw).startsWith("Bearer ") ? raw : `Bearer ${raw}`;
  return { ...headers, Authorization: token };
}

const AuthService = {

  login: async (signId, password, token, opts = {}) => {
    const { baseAuth, login } = ApiConfig;
    const url = baseAuth + login;
    const params = {
      email_or_phone: signId,
      password: password,
      token,
      bind_ip: !!opts.bindIp,
    };
    return ApiCallPost(url, params, deviceAuthHeaders());
  },

  registerEmail: async (signId, password, invitation, token) => {
    const { baseAuth, registerEmail } = ApiConfig;
    const url = baseAuth + registerEmail;
    const params = {
      email: signId,
      password: password,
      referral_code: invitation,
      token: token,
      terms_accepted: true,
    };
    return ApiCallPost(url, params, deviceAuthHeaders());
  },


  registerPhone: async (signId, password, invitation, countryCode, token) => {
    const { baseAuth, registerPhone } = ApiConfig;
    const url = baseAuth + registerPhone;
    const params = {
      phone: signId,
      password: password,
      referral_code: invitation,
      token: token,
      country_code: countryCode,
      terms_accepted: true,
    };
    return ApiCallPost(url, params, deviceAuthHeaders());
  },

  /**
   * POST /v1/user/check-signup-email
   * Body: { purpose, kind, email?, phone?, country_code?, referralCode? }
   * success true → continue; success false → show message.
   */
  checkIdentifier: async ({ identifier, kind, purpose = "signup", countryCode, referralCode } = {}) => {
    const { baseAuth, checkSignupEmail: path } = ApiConfig;
    const url = baseAuth + path;
    const purposeNorm = String(purpose || "signup").trim().toLowerCase() === "login" ? "login" : "signup";
    let kindNorm = String(kind || "").trim().toLowerCase();
    if (kindNorm !== "phone") kindNorm = "email";

    const value = kindNorm === "phone"
      ? String(identifier || "").replace(/\D/g, "").replace(/^0+/, "")
      : String(identifier || "").trim();

    const params = { purpose: purposeNorm, kind: kindNorm };
    if (kindNorm === "phone") {
      params.phone = value;
      params.country_code = String(countryCode || "+91").trim();
    } else {
      params.email = value;
    }

    const ref = String(referralCode || "").trim();
    if (purposeNorm === "signup" && ref) params.referralCode = ref;
    return ApiCallPost(url, params, deviceAuthHeaders());
  },

  /** @deprecated use checkIdentifier — kept for callers passing email + referral only */
  checkSignupEmail: async (email, referralCode) =>
    AuthService.checkIdentifier({
      identifier: email,
      kind: "email",
      purpose: "signup",
      referralCode,
    }),

  joinWaitlist: async (payload = {}) => {
    const { baseUrl, joinWaitlist } = ApiConfig;
    const url = `${baseUrl}v1/${joinWaitlist}`;
    return ApiCallPost(url, payload, deviceAuthHeaders());
  },

  registrationOtp: async (signId, registeredBy) => {
    const { baseAuth, registrationOtp } = ApiConfig;
    const url = baseAuth + registrationOtp;
    const params = {
      signId: signId, registeredBy
    };
    return ApiCallPost(url, params, deviceAuthHeaders());
  },

  verifyRegistrationToken: async (authenticationToken) => {
    const token = authenticationToken
    const { baseAuth, verifyRegistrationToken } = ApiConfig;
    const url = baseAuth + verifyRegistrationToken;
    const headers = {
      ...deviceAuthHeaders(),
      Authorization: token,
    };
    return ApiCallGetVerifyRegistration(url, headers);
  },

  getOtp: async (signid, type, sendTo = null) => {
    const { baseAuth, getOtp: otpEndpoint } = ApiConfig;
    const url = baseAuth + otpEndpoint;
    const params = {
      email_or_phone: signid,
      type: type,
      "resend": true
    };
    // For login flow, specify which method to send OTP to (email or mobile)
    if (sendTo) {
      params.sendTo = sendTo;
    }
    return ApiCallPost(url, params, deviceAuthHeaders());
  },

  verifyRegistrationOtp: async (signId, verification_code, registeredBy, token) => {
    const { baseAuth, verifyRegistrationOtp } = ApiConfig;
    const url = baseAuth + verifyRegistrationOtp;
    const params = {
      signId: signId, verification_code, token, registeredBy
    };
    return ApiCallPost(url, params, deviceAuthHeaders());
  },

  /**
   * Login verify-otp. Pass challengeOpts: { challenge_id, tempToken }.
   */
  getCode: async (signId, type, otp, challengeOpts = {}) => {
    const { baseAuth, getcode } = ApiConfig;
    const url = baseAuth + getcode;
    const params = {
      "email_or_phone": signId,
      "type": type,
      // For Google Auth (type 2), keep as string to preserve leading zeros
      // For Email/Mobile OTP (type 1 & 3), convert to number
      "otp": type === 2 ? otp : parseInt(otp, 10)
    };
    if (challengeOpts.challenge_id) params.challenge_id = challengeOpts.challenge_id;
    if (challengeOpts.tempToken) params.tempToken = challengeOpts.tempToken;
    return ApiCallPost(url, params, deviceAuthHeaders());
  },

  /**
   * Lost Login 2-Step method — POST /v1/user/login-methods/recovery/start
   * Auth: tempToken + challenge_id + X-Device-Id (no Bearer).
   * @param {{ tempToken: string, challenge_id?: string, lost_method: "totp"|"email"|"phone" }} body
   */
  loginMethodRecoveryStart: async ({ tempToken, challenge_id, lost_method } = {}) => {
    const { baseAuth, loginMethodRecoveryStart: path } = ApiConfig;
    const url = baseAuth + path;
    const params = {
      tempToken: String(tempToken || ""),
      lost_method: String(lost_method || "").toLowerCase(),
    };
    if (challenge_id) params.challenge_id = String(challenge_id);
    return ApiCallPost(url, params, deviceAuthHeaders());
  },

  /**
   * Prove a remaining recovery method — POST /v1/user/login-methods/recovery/verify
   * Last verify returns tokens (`verification_complete: true`).
   * @param {{ tempToken: string, challenge_id?: string, method: "totp"|"email"|"phone"|"mobile", code: string }} body
   */
  loginMethodRecoveryVerify: async ({ tempToken, challenge_id, method, code } = {}) => {
    const { baseAuth, loginMethodRecoveryVerify: path } = ApiConfig;
    const url = baseAuth + path;
    const params = {
      tempToken: String(tempToken || ""),
      method: String(method || "").toLowerCase(),
      code: String(code ?? ""),
      otp: String(code ?? ""),
    };
    if (challenge_id) params.challenge_id = String(challenge_id);
    return ApiCallPost(url, params, deviceAuthHeaders());
  },

  forgotPassword: async (inputData, type, countryCode = "+91") => {
    const { baseAuth, forgotpassword } = ApiConfig;
    const url = baseAuth + forgotpassword;
    const fullPhonePayload = `${countryCode} ${inputData?.signId}`;
    const params = {
      email_or_phone: type === "phone" ? fullPhonePayload : inputData?.signId,
      verification_code: +inputData?.otp,
      new_password: inputData?.password,
    };
    return ApiCallPost(url, params, deviceAuthHeaders());
  },

  googleLogin: async (tokenResponse, captchaData, opts = {}) => {
    const { baseAuth, loginwithGoogle } = ApiConfig;
    const url = baseAuth + loginwithGoogle;
    const params = {
      Token: tokenResponse?.access_token,
      type: 'google',
      bind_ip: !!opts.bindIp,
    };
    return ApiCallPost(url, params, deviceAuthHeaders());
  },

  signupwithGoogle: async (tokenResponse, captchaData, invitation) => {
    const { baseAuth, signupwithGoogle } = ApiConfig;
    const url = baseAuth + signupwithGoogle;
    const params = {
      Token: tokenResponse?.access_token,
      type: 'google', referral_code: invitation
    };
    return ApiCallPost(url, params, deviceAuthHeaders());
  },

  appleLogin: async (applePayload, opts = {}) => {
    const { baseAuth, loginwithGoogle } = ApiConfig;
    const url = baseAuth + loginwithGoogle;
    const params = {
      Token: applePayload?.idToken || applePayload?.authorization?.id_token,
      type: 'apple',
      bind_ip: !!opts.bindIp,
    };
    if (applePayload?.code) params.code = applePayload.code;
    if (applePayload?.user) params.user = applePayload.user;
    console.log("[Apple login] API payload", { url, params });
    const result = await ApiCallPost(url, params, deviceAuthHeaders());
    console.log("[Apple login] API response", result);
    return result;
  },

  signupwithApple: async (applePayload, invitation) => {
    const { baseAuth, signupwithGoogle } = ApiConfig;
    const url = baseAuth + signupwithGoogle;
    const params = {
      Token: applePayload?.idToken || applePayload?.authorization?.id_token,
      type: 'apple',
      referral_code: invitation,
    };
    if (applePayload?.code) params.code = applePayload.code;
    if (applePayload?.user) params.user = applePayload.user;
    console.log("[Apple signup] API payload", { url, params });
    const result = await ApiCallPost(url, params, deviceAuthHeaders());
    console.log("[Apple signup] API response", result);
    return result;
  },

  refreshSession: async (refreshToken) => {
    const url = `${ApiConfig.baseSecurity}auth/refresh`;
    const params = { refresh_token: refreshToken };
    return ApiCallPost(url, params, deviceAuthHeaders());
  },


  getActivityLogs: async (skip, limit) => {
    const token = localStorage.getItem("token");
    const { baseAuth, activityLogs } = ApiConfig;
    let url = baseAuth + activityLogs;
    const qs = [];
    if (skip != null && `${skip}` !== "") qs.push(`skip=${encodeURIComponent(String(skip))}`);
    if (limit != null && `${limit}` !== "") qs.push(`limit=${encodeURIComponent(String(limit))}`);
    if (qs.length) url += `?${qs.join("&")}`;
    const headers = {
      ...(token ? { Authorization: token } : {}),
    };
    return ApiCallGet(url, headers);
  },


  accessTokenSumsub: async () => {
    const token = localStorage.getItem("token");
    const { baseAuth, accessTokenSumsub } = ApiConfig;
    const url = baseAuth + accessTokenSumsub;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  setCurrency: async (currencyType) => {
    const token = localStorage.getItem("token");
    const { baseAuth, setCurrency } = ApiConfig;
    const url = baseAuth + setCurrency;
    const params = {
      currency: currencyType,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPut(url, params, headers);
  },

  transferToSpot: async (selectedCurrency, withdrawAmount) => {
    const token = localStorage.getItem("token");
    const { baseP2p, transferFunds } = ApiConfig;
    const url = baseP2p + transferFunds;
    const params = {
      spot_wallet: true,
      amount: +withdrawAmount,
      short_name: selectedCurrency
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  transferToP2P: async (selectedCurrency, withdrawAmount) => {
    const token = localStorage.getItem("token");
    const { baseP2p, transferFunds } = ApiConfig;
    const url = baseP2p + transferFunds;
    const params = {
      funding_wallet: true,
      amount: +withdrawAmount,
      short_name: selectedCurrency
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  getUserfunds: async (type) => {
    const { baseWallet, userfunds } = ApiConfig;
    const url = baseWallet + userfunds + `?wallet_type=${encodeURIComponent(type || "")}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  userP2pWallet: async () => {
    const token = localStorage.getItem("token");
    const { baseWallet, userP2pWallet } = ApiConfig;
    const url = baseWallet + userP2pWallet;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  availableWalletTypes: async () => {
    const token = localStorage.getItem("token");
    const { baseWallet, availableWalletTypes } = ApiConfig;
    const url = baseWallet + availableWalletTypes;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  getPerticularWalletBalance: async (currencyId, fromWallet, toWallet) => {
    const token = localStorage.getItem("token");
    const { baseWallet, getPerticularWalletBalance } = ApiConfig;
    const url = baseWallet + getPerticularWalletBalance + `?fromWallet=${fromWallet}&toWallet=${toWallet}&currencyId=${currencyId}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  getWalletBalance: async (currencyId, fromWallet) => {
    const token = localStorage.getItem("token");
    const { baseWallet, getWalletBalance } = ApiConfig;
    const url = baseWallet + getWalletBalance + `?fromWallet=${fromWallet}&currencyId=${currencyId}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  walletTransferHistory: async (params = {}) => {
    const token = localStorage.getItem("token");
    const { baseWallet, walletTransferHistory: path } = ApiConfig;
    const qp = new URLSearchParams();

    const limitRaw = params.limit != null ? Number(params.limit) : 10;
    const limit = Number.isFinite(limitRaw) ? Math.min(100, Math.max(1, Math.floor(limitRaw))) : 10;
    qp.set("limit", String(limit));

    const pageNum = params.page != null ? Number(params.page) : NaN;
    if (Number.isFinite(pageNum) && pageNum >= 1) {
      qp.set("page", String(Math.floor(pageNum)));
    } else if (params.skip != null && params.skip !== "") {
      const skipRaw = Number(params.skip);
      const skip = Number.isFinite(skipRaw) && skipRaw >= 0 ? Math.floor(skipRaw) : 0;
      qp.set("skip", String(skip));
    } else {
      qp.set("page", "1");
    }

    const fw = params.from_wallet != null ? String(params.from_wallet).trim() : "";
    if (fw && fw.toLowerCase() !== "all") qp.set("from_wallet", fw);

    const tw = params.to_wallet != null ? String(params.to_wallet).trim() : "";
    if (tw && tw.toLowerCase() !== "all") qp.set("to_wallet", tw);

    const cur = params.currency != null ? String(params.currency).trim() : "";
    if (cur && cur.toLowerCase() !== "all") qp.set("currency", cur);

    const url = `${baseWallet}${path}?${qp.toString()}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },
  bonusHistory: async (skip, limit) => {
    const token = localStorage.getItem("token");
    const { baseWallet, bonusHistory } = ApiConfig;
    const url = baseWallet + bonusHistory + `?skip=${skip}&limit=${limit}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  walletHistory: async (skip, limit) => {
    const token = localStorage.getItem('token');
    const { baseTrans, walletHistory } = ApiConfig;
    const url = baseTrans + walletHistory + `?skip=${skip}&limit=${limit}`;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  tradeHistory: async (skip, limit) => {
    const token = localStorage.getItem('token');
    const { baseTrans } = ApiConfig;
    const url = baseTrans + `trade-history?skip=${skip}&limit=${limit}`;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  /** Spot trade fills export JSON — `GET /spot/v1/me/trades/export` (replaces legacy `/v1/transaction/trade-history-export`). */
  tradeHistoryExport: async (from, to, limit = 10000) => {
    const { baseSpot } = ApiConfig;
    const u = new URLSearchParams({
      from: String(from),
      to: String(to),
      limit: String(limit),
    });
    const url = `${baseSpot}me/trades/export?${u.toString()}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  walletHistoryExport: async (from, to, type = 'all', limit = 10000) => {
    const token = localStorage.getItem('token');
    const { baseTrans, walletHistoryExport } = ApiConfig;
    const params = new URLSearchParams({ from, to, limit: String(limit) });
    if (type && type !== 'all') params.set('type', type);
    const url = `${baseTrans}${walletHistoryExport}?${params.toString()}`;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  blogList: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, blogList } = ApiConfig;
    const url = baseAuth + blogList;

    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);

  },
  /** @deprecated Use userTokenTransactionHistory */
  launchpadTransHistory: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, userTokenTransactionHistory } = ApiConfig;
    const url = baseAuth + userTokenTransactionHistory + '?page=1&limit=50';
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallGet(url, headers);
  },
  checkGiveawayStatus: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, checkGiveawayStatus } = ApiConfig;
    const url = baseAuth + checkGiveawayStatus;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);

  },



  allOpenOrder: async (skip, limit) => {
    const token = localStorage.getItem('token');
    const { baseExchange } = ApiConfig;
    const url = baseExchange + `all-open-orders?skip=${skip}&limit=${limit}`;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },



  /**
   * `POST /v1/wallet/get-and-generate-address` — get or create custodial deposit address.
   * Body: `{ assetId, short_name, generate }` — `generate`: true = ensure/create address; false = existing only.
   */
  getAndGenerateAddress: async (assetId, short_name = "", generate = false) => {
    const { baseWallet, getAndGenerateAddress: pathKey } = ApiConfig;
    const url = baseWallet + pathKey;
    const params = {
      assetId: String(assetId ?? "").trim(),
      short_name: short_name != null ? String(short_name) : "",
      generate: generate === true,
    };
    return ApiCallPost(url, params, bearerAuthHeaders());
  },

  /**
   * Legacy: `PUT /v1/wallet/generate-address` — Fireblocks custodial deposit address (kept for back-compat).
   * @param {boolean} generate - `true`: ensure vault + asset and return address. `false` / omitted: return existing only (`data` may be null).
   * @param {string} chain - BEP20 | ERC20 | POLYGON | TRC20 | BTC | SOLANA
   * @param {string} [short_name] - optional; must match an active Currency for that chain
   * @param {string} [token_asset_id] - optional; workspace-specific Fireblocks token asset id (recommended)
   */
  generateAddress: async (generate, chain, short_name, token_asset_id) => {
    const { baseWallet, generateAddress: generateAddressPath } = ApiConfig;
    const url = baseWallet + generateAddressPath;
    const params = {
      generate: generate === true,
      chain: String(chain),
      ...(short_name != null && short_name !== "" ? { short_name: String(short_name) } : {}),
      ...(token_asset_id != null && token_asset_id !== "" ? { token_asset_id: String(token_asset_id) } : {}),
    };
    return ApiCallPut(url, params, bearerAuthHeaders());
  },

  /**
   * `GET /v1/wallet/fireblocks-deposit-networks?symbol=USDT` — dynamic networks (workspace source of truth).
   */
  getFireblocksDepositNetworks: async (symbol) => {
    const { baseWallet, fireblocksDepositNetworks } = ApiConfig;
    const q = symbol != null && String(symbol).trim() ? `?symbol=${encodeURIComponent(String(symbol).trim())}` : "";
    const url = `${baseWallet}${fireblocksDepositNetworks}${q}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  /**
   * `GET /v1/wallet/fireblocks-deposit-chain-types?symbol=USDT` — legacy-friendly buckets + networks.
   */
  getFireblocksDepositChainTypes: async (symbol) => {
    const { baseWallet, fireblocksDepositChainTypes } = ApiConfig;
    const q = symbol != null && String(symbol).trim() ? `?symbol=${encodeURIComponent(String(symbol).trim())}` : "";
    const url = `${baseWallet}${fireblocksDepositChainTypes}${q}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  estimatedPortfolio: async (type) => {
    const token = localStorage.getItem("token");
    const { baseWallet, estimatedPortfolio } = ApiConfig;
    const walletParam = encodeURIComponent(String(type ?? "").trim().toLowerCase());
    const url = baseWallet + estimatedPortfolio + `?walletType=${walletParam}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  allWalletsPortfolio: async () => {
    const token = localStorage.getItem("token");
    const { baseWallet, allWalletsPortfolio } = ApiConfig;
    const url = baseWallet + allWalletsPortfolio;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  nexbAmount: async (baseCurrencyId, quoteCurrencyId, userId, usdtCoin) => {
    console.log("apiii")
    const token = localStorage.getItem("token");
    const { baseUrl, convertToken } = ApiConfig;
    const url = baseUrl + convertToken;
    const params = {
      userId: userId,
      base_Currency_id: baseCurrencyId,
      quote_Currency_id: quoteCurrencyId,
      amountToPay: usdtCoin,
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },
  claimGiveaway: async (tsize, deliveryAddress) => {
    const token = localStorage.getItem("token");
    const { baseAuth, claimGiveaway } = ApiConfig;
    const url = baseAuth + claimGiveaway;
    const params = {
      tshirtSize: tsize,
      devliveryAddress: deliveryAddress,

    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },


  swapAmount: async (baseCurrencyId, quoteCurrencyId, userId, usdtCoin, nexbCoin) => {
    const token = localStorage.getItem("token");
    const { baseUrl, swapToken } = ApiConfig;
    const url = baseUrl + swapToken;
    const params = {
      userId: userId,
      base_Currency_id: baseCurrencyId,
      quote_Currency_id: quoteCurrencyId,
      amountToPay: usdtCoin,
      amountToBuy: nexbCoin,
      fromCurrency: "USDT",
      toCurrency: "NEXB",
      side: "BUY"
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },



  withdrawalCurrency: async (walletAddress, amountValue, otp, selectedChain, signId, balance) => {
    const token = localStorage.getItem("token");
    const { baseWallet, withdrawalcurrencypath } = ApiConfig;
    const url = baseWallet + withdrawalcurrencypath;
    const params = {
      withdrawal_address: walletAddress,
      amount: amountValue,
      chain: selectedChain?.chain,
      coinName: selectedChain?.name,
      verification_code: +otp,
      email_or_phone: signId,
      usdt_balance: balance,
    };
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallPost(url, params, headers);
  },

  walletTransfer: async (fromWallet, toWallet, currencyId, amount) => {
    const token = localStorage.getItem("token");
    const { baseWallet, walletTransfer } = ApiConfig;
    const url = baseWallet + walletTransfer;
    const params = {
      fromWallet, toWallet, currencyId, amount
    };
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallPost(url, params, headers);
  },

  /** GET /v1/wallet/transfer-balance — unified transferable ceiling for any wallet route */
  getTransferBalance: async ({ fromWallet, toWallet, currencyId, pairId, assetType } = {}) => {
    const { baseWallet, transferBalance } = ApiConfig;
    const qs = new URLSearchParams({
      from_wallet: fromWallet,
      to_wallet: toWallet,
      currency_id: currencyId,
    });
    if (pairId) qs.set("pair_id", pairId);
    if (assetType) qs.set("asset_type", assetType);
    return ApiCallGet(`${baseWallet}${transferBalance}?${qs.toString()}`, bearerAuthHeaders());
  },

  /** POST /v1/wallet/transfer — unified transfer for all wallet routes */
  walletTransferUnified: async ({ fromWallet, toWallet, currencyId, amount, pairId, assetType } = {}) => {
    const { baseWallet, walletTransferUnified: path } = ApiConfig;
    const body = {
      from_wallet: fromWallet,
      to_wallet: toWallet,
      currency_id: currencyId,
      amount: String(amount),
    };
    if (pairId) body.pair_id = pairId;
    if (assetType) body.asset_type = assetType;
    return ApiCallPost(`${baseWallet}${path}`, body, bearerAuthHeaders());
  },

  marginAccounts: async () => {
    const { baseMargin, marginAccounts } = ApiConfig;
    const url = baseMargin + marginAccounts;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  // ── Cross Margin ──────────────────────────────────────────────────────────
  crossAccount: async () =>
    ApiCallGet(`${ApiConfig.baseCross}account`, bearerAuthHeaders()),

  crossTransfer: async ({ currency_id, amount, direction, walletType = "spot" }) =>
    ApiCallPost(
      `${ApiConfig.baseCross}transfer`,
      { currency_id, amount: String(amount), direction, walletType },
      bearerAuthHeaders()
    ),

  crossTransferHistory: async ({ page = 1, limit = 20, direction, currency_id } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (direction)   p.set("direction", direction);
    if (currency_id) p.set("currency_id", currency_id);
    return ApiCallGet(`${ApiConfig.baseCross}transfer-history?${p}`, bearerAuthHeaders());
  },

  crossBorrow: async ({ currency_id, amount }) =>
    ApiCallPost(`${ApiConfig.baseCross}borrow`, { currency_id, amount: String(amount) }, bearerAuthHeaders()),

  crossRepay: async ({ currency_id, amount }) => {
    const body = { currency_id };
    if (amount != null) body.amount = String(amount);   // omit to repay full debt
    return ApiCallPost(`${ApiConfig.baseCross}repay`, body, bearerAuthHeaders());
  },

  crossDebts: async () =>
    ApiCallGet(`${ApiConfig.baseCross}debts`, bearerAuthHeaders()),

  crossBorrowable: async (currency_id) => {
    const p = new URLSearchParams({ currency_id });
    return ApiCallGet(`${ApiConfig.baseCross}borrowable?${p}`, bearerAuthHeaders());
  },

  crossPositions: async () =>
    ApiCallGet(`${ApiConfig.baseCross}positions`, bearerAuthHeaders()),

  crossRisk: async () =>
    ApiCallGet(`${ApiConfig.baseCross}risk`, bearerAuthHeaders()),

  crossPnl: async (period = "all") =>
    ApiCallGet(`${ApiConfig.baseCross}pnl?period=${period}`, bearerAuthHeaders()),

  // ── Cross Margin — Orders / Trades ──────────────────────────────────────────
  crossPlaceOrder: async (payload) =>
    ApiCallPost(`${ApiConfig.baseCross}order`, payload, bearerAuthHeaders()),

  // One-click close: server reads net from balance sheet and places the correct opposing MARKET order
  crossClosePosition: async ({ pair, pair_id, quantity } = {}) => {
    const body = {};
    if (pair)      body.pair     = pair;
    if (pair_id)   body.pair_id  = pair_id;
    if (quantity != null) body.quantity = String(quantity);  // omit for full close
    return ApiCallPost(`${ApiConfig.baseCross}position/close`, body, bearerAuthHeaders());
  },

  crossCancelOrder: async (orderId) =>
    ApiCallDelete(`${ApiConfig.baseCross}order/${orderId}`, bearerAuthHeaders()),

  crossOpenOrders: async ({ pair, page = 1, limit = 100 } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (pair) p.set("pair", pair);
    return ApiCallGet(`${ApiConfig.baseCross}orders/open?${p}`, bearerAuthHeaders());
  },

  crossOrderHistory: async ({ pair, status, start_date, end_date, page = 1, limit = 20 } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (pair)       p.set("pair", pair);
    if (status)     p.set("status", status);
    if (start_date) p.set("start_date", start_date);
    if (end_date)   p.set("end_date", end_date);
    return ApiCallGet(`${ApiConfig.baseCross}orders/history?${p}`, bearerAuthHeaders());
  },

  crossTrades: async ({ pair, side, start_date, end_date, page = 1, limit = 20 } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (pair)       p.set("pair", pair);
    if (side && side !== "All") p.set("side", side);
    if (start_date) p.set("start_date", start_date);
    if (end_date)   p.set("end_date", end_date);
    return ApiCallGet(`${ApiConfig.baseCross}trades?${p}`, bearerAuthHeaders());
  },

  crossBorrowHistory: async ({ page = 1, limit = 100, asset, currency_id, start_date, end_date } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (asset)       p.set("asset", asset);
    if (currency_id) p.set("currency_id", currency_id);
    if (start_date)  p.set("start_date", start_date);
    if (end_date)    p.set("end_date", end_date);
    return ApiCallGet(`${ApiConfig.baseCross}history/borrow?${p}`, bearerAuthHeaders());
  },

  crossRepayHistory: async ({ page = 1, limit = 100, asset, currency_id, start_date, end_date } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (asset)       p.set("asset", asset);
    if (currency_id) p.set("currency_id", currency_id);
    if (start_date)  p.set("start_date", start_date);
    if (end_date)    p.set("end_date", end_date);
    return ApiCallGet(`${ApiConfig.baseCross}history/repay?${p}`, bearerAuthHeaders());
  },

  crossInterestHistory: async ({ page = 1, limit = 100, asset, currency_id, start_date, end_date } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (asset)       p.set("asset", asset);
    if (currency_id) p.set("currency_id", currency_id);
    if (start_date)  p.set("start_date", start_date);
    if (end_date)    p.set("end_date", end_date);
    return ApiCallGet(`${ApiConfig.baseCross}history/interest?${p}`, bearerAuthHeaders());
  },

  crossLedger: async ({ page = 1, limit = 100, asset, currency_id, start_date, end_date } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (asset)       p.set("asset", asset);
    if (currency_id) p.set("currency_id", currency_id);
    if (start_date)  p.set("start_date", start_date);
    if (end_date)    p.set("end_date", end_date);
    return ApiCallGet(`${ApiConfig.baseCross}history/ledger?${p}`, bearerAuthHeaders());
  },

  marginAccount: async (pairId) => {
    return ApiCallGet(`${ApiConfig.baseMargin}account/${pairId}`, bearerAuthHeaders());
  },

  marginBorrow: async (pairId, assetType, amount) =>
    ApiCallPost(`${ApiConfig.baseMargin}borrow`, { pairId, assetType, amount: String(amount) }, bearerAuthHeaders()),

  marginRepay: async (payload) =>
    ApiCallPost(`${ApiConfig.baseMargin}repay`, payload, bearerAuthHeaders()),

  marginBorrowHistory: async ({ pairId, from, to, page = 1, limit = 100 } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (pairId) p.set("pairId", pairId);
    if (from)   p.set("from", from);
    if (to)     p.set("to", to);
    return ApiCallGet(`${ApiConfig.baseMargin}history/borrow?${p}`, bearerAuthHeaders());
  },

  marginRepayHistory: async ({ pairId, from, to, page = 1, limit = 100 } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (pairId) p.set("pairId", pairId);
    if (from)   p.set("from", from);
    if (to)     p.set("to", to);
    return ApiCallGet(`${ApiConfig.baseMargin}history/repay?${p}`, bearerAuthHeaders());
  },

  marginInterestHistory: async ({ pairId, from, to, page = 1, limit = 100 } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (pairId) p.set("pairId", pairId);
    if (from)   p.set("from", from);
    if (to)     p.set("to", to);
    return ApiCallGet(`${ApiConfig.baseMargin}history/interest?${p}`, bearerAuthHeaders());
  },

  marginTransferHistory: async ({ pairId, direction, from, to, page = 1, limit = 100 } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (pairId)    p.set("pairId", pairId);
    if (direction) p.set("direction", direction);
    if (from)      p.set("from", from);
    if (to)        p.set("to", to);
    return ApiCallGet(`${ApiConfig.baseMargin}history/transfer?${p}`, bearerAuthHeaders());
  },

  marginLoans: async ({ pair } = {}) => {
    const p = new URLSearchParams();
    if (pair) p.set("pair", pair);
    const qs = p.toString();
    return ApiCallGet(`${ApiConfig.baseMargin}loans${qs ? `?${qs}` : ""}`, bearerAuthHeaders());
  },

  marginPlaceOrder: async (payload) =>
    ApiCallPost(`${ApiConfig.baseMargin}order`, payload, bearerAuthHeaders()),

  marginCancelOrder: async (orderId) =>
    ApiCallDelete(`${ApiConfig.baseMargin}order/${orderId}`, bearerAuthHeaders()),

  marginOpenOrders: async ({ pair, page = 1, limit = 100 } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (pair) p.set("pair", pair);
    return ApiCallGet(`${ApiConfig.baseMargin}orders/open?${p}`, bearerAuthHeaders());
  },

  marginOrderHistory: async ({ pair, status, start_date, end_date, page = 1, limit = 20 } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (pair)       p.set("pair", pair);
    if (status)     p.set("status", status);
    if (start_date) p.set("start_date", start_date);
    if (end_date)   p.set("end_date", end_date);
    return ApiCallGet(`${ApiConfig.baseMargin}orders/history?${p}`, bearerAuthHeaders());
  },

  marginTrades: async ({ pair, side, start_date, end_date, page = 1, limit = 20 } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (pair)       p.set("pair", pair);
    if (side && side !== "All") p.set("side", side);
    if (start_date) p.set("start_date", start_date);
    if (end_date)   p.set("end_date", end_date);
    return ApiCallGet(`${ApiConfig.baseMargin}trades?${p}`, bearerAuthHeaders());
  },

  marginPosition: async (pair) =>
    ApiCallGet(`${ApiConfig.baseMargin}position/${encodeURIComponent(pair)}`, bearerAuthHeaders()),

  marginClosePosition: async (pair, body) =>
    ApiCallPost(`${ApiConfig.baseMargin}position/${encodeURIComponent(pair)}/close`, body, bearerAuthHeaders()),

  marginPositions: async () =>
    ApiCallGet(`${ApiConfig.baseMargin}positions`, bearerAuthHeaders()),

  marginPositionHistory: async (pair, { page = 1, limit = 20, start_date, end_date } = {}) => {
    const p = new URLSearchParams({ page, limit });
    if (start_date) p.set("start_date", start_date);
    if (end_date)   p.set("end_date", end_date);
    // If no pair provided, use the all-positions history endpoint
    const url = pair
      ? `${ApiConfig.baseMargin}position/${encodeURIComponent(pair)}/history?${p}`
      : `${ApiConfig.baseMargin}positions/history?${p}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  marginWalletTransfer: async (pairId, assetType, amount, direction, walletType) => {
    const { baseMargin, marginWalletTransfer } = ApiConfig;
    const url = baseMargin + marginWalletTransfer;
    const params = { pairId, assetType, amount: String(amount), direction, walletType };
    return ApiCallPost(url, params, bearerAuthHeaders());
  },

  marginMaxTransfer: async (pairId, assetType, direction, walletType) => {
    const { baseMargin, marginMaxTransfer } = ApiConfig;
    const params = new URLSearchParams({ pairId, assetType, direction });
    if (walletType) params.set("walletType", walletType);
    const url = `${baseMargin}${marginMaxTransfer}?${params.toString()}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  marginPortfolioSummary: async () => {
    const { baseMargin, marginPortfolioSummary } = ApiConfig;
    return ApiCallGet(baseMargin + marginPortfolioSummary, bearerAuthHeaders());
  },

  marginWalletBalances: async () => {
    const { baseMargin, marginWalletBalances } = ApiConfig;
    return ApiCallGet(baseMargin + marginWalletBalances, bearerAuthHeaders());
  },

  addkyc: async (formData) => {
    const token = localStorage.getItem("token");
    const { baseAuth, identity } = ApiConfig;
    const url = baseAuth + identity;
    const headers = {
      "Content-Type": "multipart/form-data",
      Authorization: token,
    };
    return ApiCallPost(url, formData, headers);
  },

  // KYC API methods
  getCountries: async () => {
    const { baseUrl } = ApiConfig;
    const url = baseUrl + "api/meta/countries";
    const headers = {
      "Content-Type": "application/json",
    };
    return ApiCallGet(url, headers);
  },

  getKycConfig: async (countryCode) => {
    const token = localStorage.getItem("token");
    const { baseUrl } = ApiConfig;
    const url = baseUrl + `api/kyc/config/${countryCode}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * GET /v1/user/kyc-status — legacy KYC state (compatibility; see /api/v1/kyc for Didit)
   */
  getKycStatus: async () => {
    const token = localStorage.getItem("token");
    const { baseAuth, userKycStatusPath } = ApiConfig;
    const url = baseAuth + userKycStatusPath;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * `GET /v1/user/vip` — `spot` (default), `futures`, or `options`.
   * Spot and options fees are percents. Futures fees are fractions (0.0002 = 0.02%).
   */
  getMyVip: async (product) => {
    const { baseAuth, userVip } = ApiConfig;
    const productQuery = product === "futures" || product === "spot" || product === "options" ? `?product=${product}` : "";
    return ApiCallGet(`${baseAuth}${userVip}${productQuery}`, {
      "Content-Type": "application/json",
      ...bearerAuthHeaders(),
    });
  },

  /**
   * GET /v1/user/submit-kyc — legacy submit / form state (compatibility; POST may be used for file upload)
   */
  getSubmitKyc: async () => {
    const token = localStorage.getItem("token");
    const { baseAuth, userSubmitKycGetPath } = ApiConfig;
    const url = baseAuth + userSubmitKycGetPath;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  getAnnouncementCategoryList: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, getAnnouncementCategoryList } = ApiConfig;
    const url = baseAuth + getAnnouncementCategoryList;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },
  getUpcominglpList: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, getUpcominglpList } = ApiConfig;
    const url = baseAuth + getUpcominglpList;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },
  getCancellpList: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, getCancellpList } = ApiConfig;
    const url = baseAuth + getCancellpList;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },
  getLiveListing: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, getLiveListing } = ApiConfig;
    const url = baseAuth + getLiveListing;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },
  getEndedListing: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, getEndedListing } = ApiConfig;
    const url = baseAuth + getEndedListing;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },
  getUserPurchingList: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, getUserPurchingList } = ApiConfig;
    const url = baseAuth + getUserPurchingList;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },
  getGiveawayList: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, getGiveawayList } = ApiConfig;
    const url = baseAuth + getGiveawayList;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },
  /** Launchpad listing - public, no auth. `GET /v1/launchpad/get-launchpads` */
  getLaunchpads: async () => {
    const { baseLaunchpad, getLaunchpads: endpoint } = ApiConfig;
    const url = baseLaunchpad + endpoint;
    return ApiCallGet(url, { 'Content-Type': 'application/json' });
  },
  /** Single launchpad - public, no auth. `GET /v1/launchpad/:id` */
  getLaunchpadById: async (id) => {
    const { baseLaunchpad } = ApiConfig;
    const url = baseLaunchpad + id;
    return ApiCallGet(url, { 'Content-Type': 'application/json' });
  },
  /** Invest in a launchpad. `POST /v1/launchpad/buy-token` — body `{ launchpadId, amount }` */
  buyToken: async (launchpadId, amount) => {
    const token = localStorage.getItem('token');
    const { baseLaunchpad, buyToken: endpoint } = ApiConfig;
    const url = baseLaunchpad + endpoint;
    const params = { launchpadId, amount: +amount };
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallPost(url, params, headers);
  },
  /** Current user's launchpad transaction history. `GET /v1/launchpad/my-history` */
  getMyLaunchpadHistory: async (page = 1, limit = 10) => {
    const token = localStorage.getItem('token');
    const { baseLaunchpad, launchpadMyHistory: endpoint } = ApiConfig;
    const url = baseLaunchpad + endpoint + `?page=${page}&limit=${limit}`;
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallGet(url, headers);
  },
  /** Wallet balance for a launchpad currency. `GET /v1/launchpad/user-balance?currencyId=&walletType=` */
  getLaunchpadUserBalance: async (currencyId, walletType = 'earning') => {
    const token = localStorage.getItem('token');
    const { baseLaunchpad, launchpadUserBalance: endpoint } = ApiConfig;
    let url = baseLaunchpad + endpoint;
    const params = new URLSearchParams();
    if (currencyId) params.append('currencyId', currencyId);
    if (walletType) params.append('walletType', walletType);
    if (params.toString()) url += '?' + params.toString();
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallGet(url, headers);
  },
  /** Launchpad listing - public, no auth. status: UPCOMING|LIVE|ENDED|CANCELLED */
  launchpadListing: async (status = null, page = 1, limit = 10) => {
    const { baseAuth, launchpadListing: endpoint } = ApiConfig;
    let url = baseAuth + endpoint;
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('page', page);
    params.append('limit', limit);
    if (params.toString()) url += '?' + params.toString();
    return ApiCallGet(url, { 'Content-Type': 'application/json' });
  },
  /** Dashboard launchpad listing - public */
  userLaunchpadListing: async (status = null, page = 1, limit = 10) => {
    const { baseAuth, userLaunchpadListing: endpoint } = ApiConfig;
    let url = baseAuth + endpoint;
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    params.append('page', page);
    params.append('limit', limit);
    if (params.toString()) url += '?' + params.toString();
    return ApiCallGet(url, { 'Content-Type': 'application/json' });
  },
  /** Launchpad details - auth required */
  launchpadDetails: async (id) => {
    const token = localStorage.getItem('token');
    const { baseAuth, launchpadDetails } = ApiConfig;
    const url = baseAuth + launchpadDetails + `/${id}`;
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallGet(url, headers);
  },
  /** Launchpad details - public, no auth */
  userLaunchpadDetails: async (id) => {
    const { baseAuth, userLaunchpadDetails } = ApiConfig;
    const url = baseAuth + userLaunchpadDetails + `/${id}`;
    return ApiCallGet(url, { 'Content-Type': 'application/json' });
  },
  /** @deprecated Use userLaunchpadListing instead */
  getUserlpList: async () => {
    const { baseAuth, userLaunchpadListing } = ApiConfig;
    const url = baseAuth + userLaunchpadListing + '?page=1&limit=100';
    return ApiCallGet(url, { 'Content-Type': 'application/json' });
  },
  getAnnouncementList: async (id) => {
    const token = localStorage.getItem('token');
    const { baseAuth, getAnnouncementList } = ApiConfig;
    const url = baseAuth + getAnnouncementList + `/${id}`;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },
  /** @deprecated Use launchpadDetails (auth) or userLaunchpadDetails (public) */
  userlpDetails: async (id) => {
    const token = localStorage.getItem('token');
    const { baseAuth, launchpadDetails } = ApiConfig;
    const url = baseAuth + launchpadDetails + `/${id}`;
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallGet(url, headers);
  },
  userTokenSubscriptionHistory: async (page = 1, limit = 10) => {
    const token = localStorage.getItem('token');
    const { baseAuth, userTokenSubscriptionHistory } = ApiConfig;
    let url = baseAuth + userTokenSubscriptionHistory + `?page=${page}&limit=${limit}`;
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallGet(url, headers);
  },
  /** @deprecated Use userTokenSubscriptionHistory */
  subscriptionHistory: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, userTokenSubscriptionHistory } = ApiConfig;
    const url = baseAuth + userTokenSubscriptionHistory + '?page=1&limit=50';
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallGet(url, headers);
  },
  purchaseToken: async (launchpadId, amountInvested) => {
    const token = localStorage.getItem('token');
    const { baseAuth, purchaseToken: endpoint } = ApiConfig;
    const url = baseAuth + endpoint;
    const params = { launchpadId, amountInvested: +amountInvested };
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallPost(url, params, headers);
  },
  /** @deprecated Use purchaseToken */
  tokenPurches: async (launchpadId, amountInvested) => {
    const token = localStorage.getItem('token');
    const { baseAuth, purchaseToken: endpoint } = ApiConfig;
    const url = baseAuth + endpoint;
    const params = { launchpadId, amountInvested: +amountInvested };
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallPost(url, params, headers);
  },
  userPurchasingListing: async (page = 1, limit = 10) => {
    const token = localStorage.getItem('token');
    const { baseAuth, userPurchasingListing } = ApiConfig;
    const url = baseAuth + userPurchasingListing + `?page=${page}&limit=${limit}`;
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallGet(url, headers);
  },
  userTokenTransactionHistory: async (page = 1, limit = 10) => {
    const token = localStorage.getItem('token');
    const { baseAuth, userTokenTransactionHistory } = ApiConfig;
    const url = baseAuth + userTokenTransactionHistory + `?page=${page}&limit=${limit}`;
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallGet(url, headers);
  },
  userTokenDistributedHistory: async (page = 1, limit = 10) => {
    const token = localStorage.getItem('token');
    const { baseAuth, userTokenDistributedHistory } = ApiConfig;
    const url = baseAuth + userTokenDistributedHistory + `?page=${page}&limit=${limit}`;
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallGet(url, headers);
  },
  userCurrentMainBalanceUsdt: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, userCurrentMainBalanceUsdt } = ApiConfig;
    const url = baseAuth + userCurrentMainBalanceUsdt;
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallGet(url, headers);
  },
  launchpadStats: async (id) => {
    const { baseAuth, launchpadStats } = ApiConfig;
    const url = baseAuth + launchpadStats + `/${id}`;
    return ApiCallGet(url, { 'Content-Type': 'application/json' });
  },

  announcementView: async (id) => {
    const token = localStorage.getItem('token');
    const { baseAuth, announcementView } = ApiConfig;
    const url = baseAuth + announcementView + `/${id}`;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },


  favoriteCoin: async (pairId) => {
    const token = localStorage.getItem('token');
    const { baseAuth, favoriteCoin } = ApiConfig;
    const url = baseAuth + favoriteCoin;
    const params = {
      "pair_id": pairId
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  favoriteList: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, favoriteList } = ApiConfig;
    const url = baseAuth + favoriteList;

    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);

  },

  /** `GET /v1/user/deposit-active-coins` — public; used to build coin + network picker. */
  depositActiveCoins: async () => {
    const { baseAuth, depositActiveCoins: depositActiveCoinsPath } = ApiConfig;
    const url = baseAuth + depositActiveCoinsPath;
    const headers = { "Content-Type": "application/json" };
    return ApiCallGet(url, headers);
  },

  withdrawActiveCoins: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, withdrawActiveCoins } = ApiConfig;
    const url = baseAuth + withdrawActiveCoins;

    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);

  },

  allCoins: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, allCoins } = ApiConfig;
    const url = baseAuth + allCoins;

    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);

  },

  setSecurity: async (password, conPassword, verificationcode, verifyMethod, passkeyUserId = null) => {
    const token = localStorage.getItem("token");
    const { baseAuth, setSecurity } = ApiConfig;
    const url = baseAuth + setSecurity;
    const params = {
      new_password: password,
      confirm_password: conPassword,
      verification_code: verifyMethod === 2 ? verificationcode : verificationcode, // Keep as string for Google Auth
      verify_method: verifyMethod // 1=email, 2=google auth, 3=mobile
    };
    if (verifyMethod === 4 && passkeyUserId) {
      params.passkeyUserId = String(passkeyUserId);
      params.verification_code = "";
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  updateSettings: async (formData) => {
    const token = localStorage.getItem("token");
    const { baseAuth, updateSettings } = ApiConfig;
    const url = baseAuth + updateSettings;
    ConsoleLogs(TAG + ', updateSettings', `url : ' + ${url}`);
    const headers = {
      'Content-Type': 'multipart/form-data',
      'Authorization': token
    };
    return ApiCallPut(url, formData, headers);
  },

  editavatar: async (formData) => {
    const token = localStorage.getItem("token");
    const { baseAuth, editavatar } = ApiConfig;
    const url = baseAuth + editavatar;
    ConsoleLogs(TAG + ', updateSettings', `url : ' + ${url}`);
    const headers = {
      Authorization: token,
    };
    return ApiCallPut(url, formData, headers);
  },


  editusername: async (firstName, lastName) => {
    const token = localStorage.getItem('token');
    const { baseAuth, editusername } = ApiConfig;
    const url = baseAuth + editusername;
    const params = {
      "firstName": firstName,
      "lastName": lastName,
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPut(url, params, headers);
  },

  /** GET /v1/setting/get-nickname */
  getNicknameSetting: async () => {
    const token = localStorage.getItem("token");
    const { baseSetting, settingGetNickname } = ApiConfig;
    const url = baseSetting + settingGetNickname;
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    };
    return ApiCallGet(url, headers);
  },

  /** POST /v1/setting/edit-nickname — body `{ nickname }` */
  editNicknameSetting: async (nickname) => {
    const token = localStorage.getItem("token");
    const { baseSetting, settingEditNickname } = ApiConfig;
    const url = baseSetting + settingEditNickname;
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    };
    const body = { nickname: normalizeNicknameForApi(nickname) };
    return ApiCallPost(url, body, headers);
  },

  /** GET /v1/setting/get-avatar */
  getAvatarSetting: async () => {
    const token = localStorage.getItem("token");
    const { baseSetting, settingGetAvatar } = ApiConfig;
    const url = baseSetting + settingGetAvatar;
    const headers = {
      ...(token ? { Authorization: token } : {}),
    };
    return ApiCallGet(url, headers);
  },

  /** POST /v1/setting/change-avatar — `FormData` field `profilepicture` (file) */
  changeAvatarSetting: async (formData) => {
    const token = localStorage.getItem("token");
    const { baseSetting, settingChangeAvatar } = ApiConfig;
    const url = baseSetting + settingChangeAvatar;
    const headers = {
      ...(token ? { Authorization: token } : {}),
    };
    return ApiCallPost(url, formData, headers);
  },

  /** GET /v1/setting/get-notification-settings */
  getNotificationSettingsSetting: async () => {
    const token = localStorage.getItem("token");
    const { baseSetting, settingGetNotificationSettings } = ApiConfig;
    const url = baseSetting + settingGetNotificationSettings;
    const headers = {
      ...(token ? { Authorization: token } : {}),
    };
    return ApiCallGet(url, headers);
  },

  /** POST /v1/setting/update-notification-settings — full `{ email_notification, short_message, notification_display }` */
  updateNotificationSettingsSetting: async (payload) => {
    const token = localStorage.getItem("token");
    const { baseSetting, settingUpdateNotificationSettings } = ApiConfig;
    const url = baseSetting + settingUpdateNotificationSettings;
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    };
    const body = {
      email_notification: String(payload.email_notification ?? ""),
      short_message: String(payload.short_message ?? ""),
      notification_display: String(payload.notification_display ?? ""),
    };
    return ApiCallPost(url, body, headers);
  },

  /** GET /v1/setting/get-alert-settings */
  getAlertSettingsSetting: async () => {
    const token = localStorage.getItem("token");
    const { baseSetting, settingGetAlertSettings } = ApiConfig;
    const url = baseSetting + settingGetAlertSettings;
    const headers = {
      ...(token ? { Authorization: token } : {}),
    };
    return ApiCallGet(url, headers);
  },

  /** POST /v1/setting/update-alert-settings — `{ messages, email }` matching GET shape (boolean fields). */
  updateAlertSettingsSetting: async (payload) => {
    const token = localStorage.getItem("token");
    const { baseSetting, settingUpdateAlertSettings } = ApiConfig;
    const url = baseSetting + settingUpdateAlertSettings;
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    };
    return ApiCallPost(url, payload, headers);
  },

  editemail: async (emailId, eotp) => {
    const token = localStorage.getItem('token');
    const { baseAuth, editemail } = ApiConfig;
    const url = baseAuth + editemail;
    const params = {
      "emailId": emailId,
      "eotp": eotp,
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPut(url, params, headers);
  },

  editPhone: async (mobileNumber, motp) => {
    const token = localStorage.getItem('token');
    const { baseAuth, editPhone } = ApiConfig;
    const url = baseAuth + editPhone;
    const params = {
      "mobileNumber": mobileNumber,
      "motp": motp,
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPut(url, params, headers);
  },

  /**
   * Place a spot order against the `/spot/v1/orders` engine.
   *
   * Pass a payload matching the Spot Trading Frontend Integration Guide:
   *   { pair | pair_id, type, side, quantity, price?, stop_price?,
   *     time_in_force?, client_order_id?, max_slippage_percent? }
   *
   * Decimal fields (`price`, `quantity`, `stop_price`) should be sent as
   * strings to avoid float precision loss.
   */
  placeOrder: async (payload) => {
    const token = localStorage.getItem('token');
    const { baseSpot, placeOrder } = ApiConfig;
    const url = baseSpot + placeOrder;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token ? `Bearer ${token}` : undefined,
    };
    return ApiCallPost(url, payload, headers);
  },

  getPackageList: async () => {
    const token = localStorage.getItem("token");
    const { baseUserBot, getPackageList } = ApiConfig;
    const url = baseUserBot + getPackageList;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  getActivePackage: async () => {
    const token = localStorage.getItem("token");
    const { baseUserBot, getActivePackage } = ApiConfig;
    const url = baseUserBot + getActivePackage;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  getInvestment: async () => {
    const token = localStorage.getItem("token");
    const { baseUserBot, getInvestment } = ApiConfig;
    const url = baseUserBot + getInvestment;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  pastOrder: async (base_currency_id, quote_currency_id, skip, limit) => {
    const token = localStorage.getItem('token');
    const { baseExchange, pastOrder } = ApiConfig;
    const url = baseExchange + pastOrder;
    const params = {
      "base_currency_id": base_currency_id,
      "quote_currency_id": quote_currency_id,
      "skip": skip,
      "limit": limit,
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  depositHistory: async (skip, limit) => {
    const token = localStorage.getItem('token');
    const { baseTrans, depositHistory } = ApiConfig;
    const url = baseTrans + depositHistory;
    const params = {
      "skip": skip,
      "limit": limit,
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  withdrawalHistory: async (skip, limit) => {
    const token = localStorage.getItem('token');
    const { baseTrans, withdrawalHistory } = ApiConfig;
    const url = baseTrans + withdrawalHistory;
    const params = {
      "skip": skip,
      "limit": limit,
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  /**
   * `GET /v1/transaction/wallet-history?skip=&limit=` — combined wallet ledger (incl. withdraw-related types).
   */
  walletHistoryGet: async (skip = 0, limit = 20) => {
    const token = localStorage.getItem('token');
    const { baseTrans, walletHistory } = ApiConfig;
    const url = `${baseTrans}${walletHistory}?skip=${skip}&limit=${limit}`;
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * `GET /v1/wallet/fireblocks-deposit-coins` — Fireblocks-tagged deposit coin list.
   */
  getDepositCoins: async () => {
    const token = localStorage.getItem('token');
    const { baseWallet, getDepositCoins } = ApiConfig;
    const url = baseWallet + getDepositCoins;
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * Cancel a spot order via `DELETE /spot/v1/orders/:order_id`.
   *
   * Only open orders (NEW / PARTIALLY_FILLED / PENDING / TRIGGERED) can be
   * cancelled — the backend returns 409 CONFLICT otherwise. The `200` here
   * only acknowledges that the cancel job was queued; the final
   * `status: "CANCELLED"` arrives over the WebSocket feed.
   */
  cancelOrder: async (orderId) => {
    const token = localStorage.getItem('token');
    const { baseSpot, cancelOrder } = ApiConfig;
    const url = `${baseSpot}${cancelOrder}/${encodeURIComponent(orderId)}`;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token ? `Bearer ${token}` : undefined,
    };
    return ApiCallDelete(url, headers);
  },

  /** Paginated spot open orders — `GET /spot/v1/me/orders/open`. */
  spotMeOrdersOpen: async (params = {}) => {
    const { baseSpot } = ApiConfig;
    const qs = buildSpotMeActivityQuery(params);
    const url = `${baseSpot}me/orders/open${qs ? `?${qs}` : ""}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  /** Paginated spot order history — `GET /spot/v1/me/orders/history`. Date filters apply to server `updated_at`. */
  spotMeOrdersHistory: async (params = {}) => {
    const { baseSpot } = ApiConfig;
    const qs = buildSpotMeActivityQuery(params);
    const url = `${baseSpot}me/orders/history${qs ? `?${qs}` : ""}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  /** Paginated user trade fills — `GET /spot/v1/me/trades`. */
  spotMeTrades: async (params = {}) => {
    const { baseSpot } = ApiConfig;
    const qs = buildSpotMeActivityQuery(params);
    const url = `${baseSpot}me/trades${qs ? `?${qs}` : ""}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  /** Spot wallet PnL — `GET /spot/v1/me/pnl` (`period`: 24h|7d|30d|all; optional `pair`). */
  spotMePnl: async ({ period = "all", pair } = {}) => {
    const { baseSpot } = ApiConfig;
    const p = new URLSearchParams();
    if (period) p.set("period", period);
    if (pair) p.set("pair", pair);
    const qs = p.toString();
    return ApiCallGet(`${baseSpot}me/pnl${qs ? `?${qs}` : ""}`, bearerAuthHeaders());
  },

  /** Spot PnL dashboard — `GET /spot/v1/me/pnl/analysis` (account equity delta, charts). */
  spotPnlAnalysis: async (params = {}) => {
    const { baseSpot, spotPnlAnalysis } = ApiConfig;
    const qs = buildSpotPnlAnalysisQuery(params);
    const url = `${baseSpot}${spotPnlAnalysis}${qs.toString() ? `?${qs}` : ""}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  /** Paginated daily rows — `GET /spot/v1/me/pnl/analysis/details` (newest first). */
  spotPnlAnalysisDetails: async (params = {}) => {
    const { baseSpot, spotPnlAnalysisDetails } = ApiConfig;
    const qs = buildSpotPnlAnalysisDetailsQuery(params);
    const url = `${baseSpot}${spotPnlAnalysisDetails}?${qs}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  // ─── Futures API ──────────────────────────────────────────────────────────

  /** GET /v1/futures/account — all futures margin accounts */
  futuresAccounts: async () => {
    const { baseFutures } = ApiConfig;
    return ApiCallGet(`${baseFutures}account`, bearerAuthHeaders());
  },

  /** GET /v1/futures/account/:symbol — single account (auto-creates if missing) */
  futuresAccount: async (symbol) => {
    const { baseFutures } = ApiConfig;
    return ApiCallGet(`${baseFutures}account/${encodeURIComponent(symbol)}`, bearerAuthHeaders());
  },

  /** POST /v1/futures/transfer — move funds to/from futures (legacy, kept for compatibility) */
  futuresTransfer: async (payload) => {
    const { baseFutures } = ApiConfig;
    return ApiCallPost(`${baseFutures}transfer`, payload, bearerAuthHeaders());
  },

  /** POST /v1/futures/transfer-in — transfer from external wallet into futures */
  futuresTransferIn: async (payload) => {
    const { baseFutures } = ApiConfig;
    return ApiCallPost(`${baseFutures}transfer-in`, payload, bearerAuthHeaders());
  },

  /** POST /v1/futures/transfer-out — transfer from futures to external wallet */
  futuresTransferOut: async (payload) => {
    const { baseFutures } = ApiConfig;
    return ApiCallPost(`${baseFutures}transfer-out`, payload, bearerAuthHeaders());
  },

  /** GET /v1/futures/max-transfer — returns effective_available for transfer ceiling */
  futuresMaxTransfer: async (params = {}) => {
    const { baseFutures } = ApiConfig;
    const qs = new URLSearchParams();
    if (params.currency_id)  qs.set("currency_id",  params.currency_id);
    if (params.margin_asset) qs.set("margin_asset", params.margin_asset);
    return ApiCallGet(`${baseFutures}max-transfer?${qs.toString()}`, bearerAuthHeaders());
  },

  /** GET /v1/futures/transfer-history */
  futuresTransferHistory: async (params = {}) => {
    const { baseFutures } = ApiConfig;
    const qs = new URLSearchParams();
    if (params.symbol)    qs.set("symbol",    params.symbol);
    if (params.direction) qs.set("direction",  params.direction);
    qs.set("limit", String(params.limit ?? 20));
    qs.set("page",  String(params.page  ?? 1));
    return ApiCallGet(`${baseFutures}transfer-history?${qs.toString()}`, bearerAuthHeaders());
  },

  /** POST /v1/futures/orders — place a new futures order */
  futuresPlaceOrder: async (payload) => {
    return ApiCallPost(`${ApiConfig.baseFutures}orders`, payload, bearerAuthHeaders());
  },

  /** POST /v1/futures/orders/:id/cancel — cancel an order by ID */
  futuresCancelOrder: async (orderId) => {
    return ApiCallPost(
      `${ApiConfig.baseFutures}orders/${encodeURIComponent(orderId)}/cancel`,
      {},
      bearerAuthHeaders()
    );
  },

  /** GET /v1/futures/orders/open — active orders (NEW, PENDING, PARTIALLY_FILLED) */
  futuresOpenOrders: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.symbol) qs.set("symbol", params.symbol);
    qs.set("skip",  String(params.skip  ?? 0));
    qs.set("limit", String(params.limit ?? 20));
    return ApiCallGet(`${ApiConfig.baseFutures}orders/open?${qs}`, bearerAuthHeaders());
  },

  /** GET /v1/futures/orders/history — terminal orders (FILLED, CANCELLED, REJECTED, EXPIRED) */
  futuresOrderHistory: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.symbol) qs.set("symbol", params.symbol);
    if (params.status) qs.set("status", params.status);
    if (params.from)   qs.set("from",   params.from);
    if (params.to)     qs.set("to",     params.to);
    qs.set("skip",  String(params.skip  ?? 0));
    qs.set("limit", String(params.limit ?? 20));
    return ApiCallGet(`${ApiConfig.baseFutures}orders/history?${qs}`, bearerAuthHeaders());
  },

  /** GET /v1/futures/positions/open — currently open positions */
  futuresOpenPositions: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.symbol) qs.set("symbol", params.symbol);
    qs.set("skip",  String(params.skip  ?? 0));
    qs.set("limit", String(params.limit ?? 20));
    return ApiCallGet(`${ApiConfig.baseFutures}positions/open?${qs}`, bearerAuthHeaders());
  },

  /** GET /v1/futures/positions/history — closed / liquidated positions */
  futuresPositionHistory: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.symbol) qs.set("symbol", params.symbol);
    if (params.status) qs.set("status", params.status);
    if (params.side)   qs.set("side",   params.side);
    if (params.from)   qs.set("from",   params.from);
    if (params.to)     qs.set("to",     params.to);
    qs.set("skip",  String(params.skip  ?? 0));
    qs.set("limit", String(params.limit ?? 20));
    return ApiCallGet(`${ApiConfig.baseFutures}positions/history?${qs}`, bearerAuthHeaders());
  },

  /** GET /v1/futures/trades — trade / execution history */
  futuresExecutions: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.symbol) qs.set("symbol", params.symbol);
    if (params.from)   qs.set("from",   params.from);
    if (params.to)     qs.set("to",     params.to);
    qs.set("skip",  String(params.skip  ?? 0));
    qs.set("limit", String(params.limit ?? 20));
    return ApiCallGet(`${ApiConfig.baseFutures}trades?${qs}`, bearerAuthHeaders());
  },

  /** GET /v1/futures/wallet-history — unified futures wallet statement */
  futuresWalletHistory: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.asset)  qs.set("asset",  params.asset);
    if (params.symbol) qs.set("symbol", params.symbol);
    if (params.type)   qs.set("type",   params.type);
    if (params.from)   qs.set("from",   params.from);
    if (params.to)     qs.set("to",     params.to);
    qs.set("page",  String(params.page  ?? 1));
    qs.set("limit", String(params.limit ?? 20));
    return ApiCallGet(`${ApiConfig.baseFutures}wallet-history?${qs}`, bearerAuthHeaders());
  },

  /** GET /v1/futures/symbol-settings?symbol= — Isolated/Cross + leverage for one symbol */
  futuresSymbolSettings: async (symbol) => {
    const qs = new URLSearchParams();
    if (symbol) qs.set("symbol", String(symbol).trim());
    return ApiCallGet(`${ApiConfig.baseFutures}symbol-settings?${qs}`, bearerAuthHeaders());
  },

  /** POST /v1/futures/margin-type — switch Isolated ↔ Cross for one symbol */
  futuresMarginType: async ({ symbol, margin_mode }) => {
    return ApiCallPost(
      `${ApiConfig.baseFutures}margin-type`,
      { symbol, margin_mode },
      bearerAuthHeaders()
    );
  },

  /** POST /v1/futures/leverage — set leverage for one symbol */
  futuresSetLeverage: async ({ symbol, leverage }) => {
    return ApiCallPost(
      `${ApiConfig.baseFutures}leverage`,
      { symbol, leverage: Number(leverage) },
      bearerAuthHeaders()
    );
  },

  /**
   * POST /v1/futures/leverage-margin/batch
   * Body: { symbols? | all_symbols?, margin_mode?, leverage? }
   */
  futuresLeverageMarginBatch: async (body = {}) => {
    return ApiCallPost(`${ApiConfig.baseFutures}leverage-margin/batch`, body, bearerAuthHeaders());
  },

  /** GET /v1/futures/isolated-margin?symbol= — preview current / max addable / max removable */
  futuresIsolatedMarginPreview: async (symbol) => {
    const qs = new URLSearchParams();
    if (symbol) qs.set("symbol", String(symbol).trim());
    return ApiCallGet(`${ApiConfig.baseFutures}isolated-margin?${qs}`, bearerAuthHeaders());
  },

  /** POST /v1/futures/isolated-margin — add/remove Isolated margin on open position */
  futuresIsolatedMargin: async ({ symbol, type = "add", amount }) => {
    return ApiCallPost(
      `${ApiConfig.baseFutures}isolated-margin`,
      { symbol, type, amount: amount != null ? String(amount) : amount },
      bearerAuthHeaders()
    );
  },

  /** GET /v1/futures/cross-equity — Cross wallet / UPNL / MM / ratio snapshot */
  futuresCrossEquity: async () => {
    return ApiCallGet(`${ApiConfig.baseFutures}cross-equity`, bearerAuthHeaders());
  },

  cancelAllOrder: async () => {
    const token = localStorage.getItem('token');
    const { baseExchange, cancel_all_order } = ApiConfig;
    const url = baseExchange + cancel_all_order;
    const params = {};
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  coinDetails: async (currency_id) => {
    const token = localStorage.getItem('token');
    const { baseExchange, coinDetails } = ApiConfig;
    const url = baseExchange + coinDetails;
    const params = {
      "currency_id": currency_id
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  update2fa: async (authType, code, verifyType) => {
    const token = localStorage.getItem("token");
    const { baseAuth, update2fa } = ApiConfig;
    const url = baseAuth + update2fa;
    const params = {
      type: authType,
      verification_code: Number(code),
      email_or_phone: verifyType
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPut(url, params, headers);
  },


  googleAuth: async () => {
    const token = localStorage.getItem("token");
    const { baseAuth, googleAuth } = ApiConfig;
    const url = baseAuth + googleAuth;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  getHistoricalData: async (fromSymbol, toSymbol, from, to, chartResolution) => {
    const token = localStorage.getItem('token');
    const { baseExchange, getHistoricalData } = ApiConfig;
    const url = baseExchange + getHistoricalData;
    const params = {
      base_currency: fromSymbol,
      quote_currency: toSymbol,
      from: from,
      to: to,
      limit: 2000,
      chartResolution,
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  rateOrder: async (rating) => {
    const token = localStorage.getItem("token");
    const { baseP2p, rateOrder } = ApiConfig;

    const url = baseP2p + rateOrder;
    const params = {
      ratings: rating,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };

    return ApiCallPost(url, params, headers);
  },

  editBankDetails: async (accountType, bankName, holderName, accountNumber, ifscCode, branchAddress, id) => {
    const token = localStorage.getItem("token");
    const { baseAuth, editBankDetails } = ApiConfig;
    const url = baseAuth + editBankDetails;
    const params = {
      _id: id,
      account_type: accountType,
      bank_name: bankName,
      account_holder_name: holderName,
      account_number: accountNumber,
      ifsc_code: ifscCode,
      branch_name: branchAddress,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  editUpiDetails: async (formData) => {
    const token = localStorage.getItem("token");
    const { baseAuth, editUpiDetails } = ApiConfig;
    const url = baseAuth + editUpiDetails;

    const headers = {
      "Content-Type": "multipart/form-data",
      Authorization: token,
    };
    return ApiCallPut(url, formData, headers);
  },


  cryptoCompareApi: async (base, quote, to, resolution) => {
    const token = localStorage.getItem('token');
    const url = `https://min-api.cryptocompare.com/data/v2/${resolution}?fsym=${base}&tsym=${quote}&toTs=${to}&limit=2000`
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },


  getDetails: async () => {
    const token = localStorage.getItem("token");
    const { baseAuth, getDetails } = ApiConfig;
    const url = baseAuth + getDetails;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  inrDeposit: async (formData) => {
    const token = localStorage.getItem("token");
    const { baseWallet, depositInr } = ApiConfig;
    const url = baseWallet + depositInr;
    const headers = {
      "Content-Type": "multipart/form-data",
      Authorization: token,
    };
    return ApiCallPost(url, formData, headers);
  },

  inrWithdrawal: async (withdrawAmount) => {
    const token = localStorage.getItem("token");
    const { baseWallet, withdrawalInr } = ApiConfig;
    const url = baseWallet + withdrawalInr;
    const params = {
      amount: withdrawAmount,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },


  getUserBankDetails: async () => {
    const token = localStorage.getItem("token");
    const { baseAuth, userBankDetails } = ApiConfig;
    const url = baseAuth + userBankDetails;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  quickBuySellHistory: async (skip, limit) => {
    const token = localStorage.getItem('token');
    const { baseSwap, quickBuySellHistory } = ApiConfig;
    const url = baseSwap + quickBuySellHistory + `?skip=${skip}&limit=${limit}`;
    const headers = { 'Content-Type': 'application/json', "Authorization": token };
    return ApiCallGet(url, headers);
  },

  baseCurrencyList: async () => {
    const token = localStorage.getItem('token');
    const { baseSwap, baseCurrencyList } = ApiConfig;
    const url = baseSwap + baseCurrencyList;
    const headers = { 'Content-Type': 'application/json', "Authorization": token };
    return ApiCallGet(url, headers);
  },

  getConversionRate: async (from, receive) => {
    const token = localStorage.getItem('token');
    const { baseSwap, getConversionRate } = ApiConfig;
    const url = baseSwap + getConversionRate + `?from=${from}&receive=${receive}`;
    const headers = { 'Content-Type': 'application/json', "Authorization": token };
    return ApiCallGet(url, headers);
  },

  getApk: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, getApk } = ApiConfig;
    const url = baseAuth + getApk;
    const headers = { 'Content-Type': 'application/json', "Authorization": token };
    return ApiCallGet(url, headers);
  },


  swapToken: async (fromWallet, toWallet, amount) => {
    const token = localStorage.getItem('token');
    const { baseSwap, quick_buy_sell } = ApiConfig;
    const url = baseSwap + quick_buy_sell;
    const params = {
      fromWallet, toWallet, amount
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  getPartnerName: async (partnerId) => {
    const token = localStorage.getItem("token");
    const { basePartner, getPartnerName } = ApiConfig;
    const url = basePartner + getPartnerName + `/${partnerId}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  packageList: async () => {
    const token = localStorage.getItem("token");
    const { baseEarning, packageList } = ApiConfig;
    const url = baseEarning + packageList;

    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  subscribedPackageList: async (skip, limit) => {
    const token = localStorage.getItem("token");
    const { baseEarning, subscribedPackageList } = ApiConfig;
    const url = baseEarning + subscribedPackageList + `?skip=${skip}&limit=${limit}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  earningPortfolio: async () => {
    const token = localStorage.getItem("token");
    const { baseEarning, earningPortfolio } = ApiConfig;
    const url = baseEarning + earningPortfolio;

    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  earningPortfolioSummary: async () => {
    const token = localStorage.getItem("token");
    const { baseEarning, earningPortfolioSummary } = ApiConfig;
    const url = baseEarning + earningPortfolioSummary;

    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  subscribeEarningPackage: async (planId, investAmount, walletType) => {
    const token = localStorage.getItem("token");
    const { baseEarning, subscribeEarningPackage } = ApiConfig;
    const url = baseEarning + subscribeEarningPackage;
    const params = { planId, investAmount, walletType };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  buyArbitrageBot: async (planId, walletType) => {
    const token = localStorage.getItem("token");
    const { baseUserBot, buyArbitrageBot } = ApiConfig;
    const url = baseUserBot + buyArbitrageBot;
    const params = { planId, walletType };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  addFunds: async (investAmount, walletType) => {
    const token = localStorage.getItem("token");
    const { baseUserBot, addFunds } = ApiConfig;
    const url = baseUserBot + addFunds;
    const params = { investAmount, walletType };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  getCvbotUserWallet: async (Id) => {
    const token = localStorage.getItem("token");
    const { baseCvbot, getCvbotUserWallet } = ApiConfig;
    const url = baseCvbot + getCvbotUserWallet + `/${Id}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },


  depositCvbotFunds: async (Id) => {
    const token = localStorage.getItem("token");
    const { baseCvbot, depositCvbotFunds } = ApiConfig;
    const url = baseCvbot + depositCvbotFunds;
    const params = { cvBotUuid: Id };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  sendKginOtpOtp: async (kginNumber) => {
    const token = localStorage.getItem("token");
    const { baseAuth, sendkginotp } = ApiConfig;
    const url = baseAuth + sendkginotp;
    const params = { mobileNumber: Number(kginNumber) };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  verifyKginOtpOtp: async (kginNumber, kginOtp) => {
    const token = localStorage.getItem("token");
    const { baseAuth, verifykginotp } = ApiConfig;
    const url = baseAuth + verifykginotp;
    const params = {
      mobileNumber: Number(kginNumber),
      otp: kginOtp
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  /** GET `/v1/notifications/user-notifications?page=&limit=` — Bearer. */
  notifications: async (opts) => {
    const o = opts != null && typeof opts === "object" ? opts : {};
    const page = Math.max(1, parseInt(String(o.page ?? 1), 10) || 1);
    const limit = Math.min(100, Math.max(1, parseInt(String(o.limit ?? 10), 10) || 10));
    const { baseNotification, notifiactionlist } = ApiConfig;
    const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
    const url = `${baseNotification}${notifiactionlist}?${qs.toString()}`;
    let raw = null;
    try {
      raw = localStorage.getItem("token") || sessionStorage.getItem("token");
    } catch {
      raw = null;
    }
    const token =
      raw && String(raw).trim() !== "" ? (String(raw).startsWith("Bearer ") ? raw : `Bearer ${raw}`) : null;
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = token;
    return ApiCallGet(url, headers);
  },

  /** GET `/v1/notifications/mark-all-as-read` — Bearer. */
  markasAllRead: async () => {
    const { baseNotification, markasAllRead } = ApiConfig;
    const url = baseNotification + markasAllRead;
    let raw = null;
    try {
      raw = localStorage.getItem("token") || sessionStorage.getItem("token");
    } catch {
      raw = null;
    }
    const token =
      raw && String(raw).trim() !== "" ? (String(raw).startsWith("Bearer ") ? raw : `Bearer ${raw}`) : null;
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = token;
    return ApiCallGet(url, headers);
  },

  /** POST `/v1/notifications/mark-as-read` `{ notificationId }` — Bearer. */
  markasRead: async (notificationId) => {
    const { baseNotification, markasRead } = ApiConfig;
    const url = baseNotification + markasRead;
    let raw = null;
    try {
      raw = localStorage.getItem("token") || sessionStorage.getItem("token");
    } catch {
      raw = null;
    }
    const token =
      raw && String(raw).trim() !== "" ? (String(raw).startsWith("Bearer ") ? raw : `Bearer ${raw}`) : null;
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = token;
    const params = { notificationId };
    return ApiCallPost(url, params, headers);
  },


  refferlsList: async () => {
    const token = localStorage.getItem("token");
    const { baseAuth, reffercode } = ApiConfig;
    const url = baseAuth + reffercode;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  getPairs: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, getPairs } = ApiConfig;
    const url = baseAuth + getPairs;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  getMemePairs: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, getMemePairs } = ApiConfig;
    const url = baseAuth + getMemePairs;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  getHotPairs: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, getHotPairs } = ApiConfig;
    const url = baseAuth + getHotPairs;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  get_top_GL: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, get_top_GL } = ApiConfig;
    const url = baseAuth + get_top_GL;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },


  get_trending: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, get_trending } = ApiConfig;
    const url = baseAuth + get_trending;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  p2pCoinList: async () => {
    const token = localStorage.getItem('token');
    const { baseCoin, p2pCoinList } = ApiConfig;
    const url = baseCoin + p2pCoinList;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  getCoinList: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, getCoinList } = ApiConfig;
    const url = baseAuth + getCoinList;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  /** `GET /v1/coin/get-coin` — full coins list (CurrencyController.get_all_coins). */
  getAllCoins: async () => {
    const token = localStorage.getItem("token");
    const { baseCoin, coinGetCoin } = ApiConfig;
    const url = baseCoin + coinGetCoin;
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    };
    return ApiCallGet(url, headers);
  },

  fetchPaymentMethods: async () => {
    const token = localStorage.getItem('token');
    const { baseP2p, fetchPaymentMethods } = ApiConfig;
    const url = baseP2p + fetchPaymentMethods;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  p2pBuyOrder: async (short_name) => {
    const token = localStorage.getItem('token');
    const { baseP2p, buyOrder } = ApiConfig;
    const url = baseP2p + buyOrder;
    const params = { short_name };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  p2pSellOrder: async (short_name) => {
    const token = localStorage.getItem('token');
    const { baseP2p, sellOrder } = ApiConfig;
    const url = baseP2p + sellOrder;
    const params = { short_name };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  currentPrice: async (fiat, asset) => {
    const token = localStorage.getItem('token');
    const { baseP2p, currentPrice } = ApiConfig;
    const url = baseP2p + currentPrice;
    const params = {
      "base_currency": asset,
      "quote_currency": fiat
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  fiatCurrencyList: async () => {
    const token = localStorage.getItem('token');
    const { baseP2p, fiatCurrencyList } = ApiConfig;
    const url = baseP2p + fiatCurrencyList;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  myAds: async () => {
    const token = localStorage.getItem('token');
    const { baseP2p, myAds } = ApiConfig;
    const url = baseP2p + myAds;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  myOrders: async () => {
    const token = localStorage.getItem('token');
    const { baseP2p, myOrders } = ApiConfig;
    const url = baseP2p + myOrders;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  runtime: async (status, orderId) => {
    const token = localStorage.getItem('token');
    const { baseP2p, p2pRuntime } = ApiConfig;
    const url = baseP2p + p2pRuntime;
    const params = {
      status: status,
      order_id: orderId
    };
    const headers = {
      "Authorization": token,
      'Content-Type': 'application/json',
    };
    return ApiCallPost(url, params, headers);
  },


  notify: async (orderId) => {
    const token = localStorage.getItem('token');
    const { baseP2p, notify } = ApiConfig;
    const url = baseP2p + notify;
    const params = {
      "order_id": orderId
    }

    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  addOrderNotification: async (orderId) => {
    const token = localStorage.getItem('token');
    const { baseP2p, addOrderNotification } = ApiConfig;
    const url = baseP2p + addOrderNotification;

    const params = {
      order_id: orderId
    }

    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  swapHistory: async () => {
    const token = localStorage.getItem("token");
    const { baseP2p, swapHistory } = ApiConfig;
    const url = baseP2p + swapHistory;
    const params = {
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);

  },

  notifySellerTrader: async (status, orderId) => {
    const token = localStorage.getItem('token');
    const { baseP2p, notifyMerchent } = ApiConfig;
    const url = baseP2p + notifyMerchent;
    const params = {
      order_id: orderId,
      trader_status: status,
    }

    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  notifySellerMerchent: async (status, orderId) => {
    const token = localStorage.getItem('token');
    const { baseP2p, notifySeller } = ApiConfig;
    const url = baseP2p + notifySeller;
    const params = {
      order_id: orderId,
      merchant_status: status,
    }

    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  buyRequest: async (orderId) => {
    const token = localStorage.getItem('token');
    const { baseP2p, buyRequest } = ApiConfig;
    const url = baseP2p + buyRequest;
    const params = {
      "order_id": orderId
    }

    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  buyCurrency: async (payableAmount, receivableAmount, id, postAdId, timestamp, sellerSelectedPayment, method) => {
    const token = localStorage.getItem('token');
    const { baseP2p, buyCurrency } = ApiConfig;
    const url = baseP2p + buyCurrency;
    const params = {
      "amount": payableAmount,
      "receiving_amount": receivableAmount,
      "postAd_user": postAdId,
      "add_id": id,
      "payment_timestamp": timestamp,
      "payment_type": sellerSelectedPayment,
      "payment_method": method,
    }
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  createNewPost: async (input, paymentMethod) => {
    const token = localStorage.getItem('token');
    const { baseP2p, createNewPost } = ApiConfig;
    const url = baseP2p + createNewPost;
    const params = {
      "base_currency": input?.asset,
      "quote_currency": input?.fiat,
      "side": input?.side,
      "price_type": input?.priceType,
      "fixed_price": input?.price,
      "payment_time": input?.time,
      // "min_amount": input?.minAmount,
      // "max_amount": input?.maxAmount,
      "payment_method": paymentMethod,
      "kyc": input?.kyc,
      "totalTransactions": input?.transaction,
      "counterCurrency": input?.minCurrency,
      "currencyAmount": input?.currencyAmount || 0,
      "volume": input?.volume,
      "registered_days": +input?.regDays
    }
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  assetAddList: async (projectName, contactName, telegramId, phoneNumber, emailAddress, referredBy, comments, countryCode, contractAddress) => {
    const { baseCoinList, coinListedDetails } = ApiConfig;
    const url = baseCoinList + coinListedDetails;
    const params = {
      projectName: projectName,
      contactName: contactName,
      telegramId: telegramId,
      phoneNumber: phoneNumber,
      emailId: emailAddress,
      referredBy: referredBy,
      comments: comments,
      countryCode: countryCode,
      smartContractAddress: contractAddress,
    };
    const headers = {
      "Content-Type": "application/json",
    };
    return ApiCallPost(url, params, headers);
  },

  addPartnerShipDetails: async (formData) => {
    const token = localStorage.getItem("token");
    const { basePartner, partnerships } = ApiConfig;
    const url = basePartner + partnerships;
    const headers = {
      "Content-Type": "multipart/form-data",
      Authorization: token,
    };
    return ApiCallPost(url, formData, headers);
  },

  addUpiDetails: async (formData) => {
    const token = localStorage.getItem("token");
    const { baseAuth, addUpiDetails } = ApiConfig;
    const url = baseAuth + addUpiDetails;
    const headers = {
      "Content-Type": "multipart/form-data",
      Authorization: token,
    };
    return ApiCallPost(url, formData, headers);
  },

  addBankDetails: async (account_type, bank_name, account_holder_name, account_number, ifsc_code, branch_name) => {
    const token = localStorage.getItem("token");
    const { baseAuth, addBankDetails } = ApiConfig;
    const url = baseAuth + addBankDetails;
    const params = { account_type, bank_name, account_holder_name, account_number, ifsc_code, branch_name }
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },


  registerPartner: async (emailAddress, password) => {
    const url = ApiConfig.baseUrl + 'v1/partnerShip/signUp';
    const params = {
      email: emailAddress,
      password: password
    };
    const headers = {
      "Content-Type": "application/json",
    };
    return ApiCallPost(url, params, headers);
  },

  viewBlog: async (id) => {
    const token = localStorage.getItem('token');
    const { baseAuth, viewBlog } = ApiConfig;
    const url = baseAuth + viewBlog + `/${id}`;

    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);

  },


  totalReferCount: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, total_refer_count } = ApiConfig;
    const url = baseAuth + total_refer_count;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  getReferList: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, refer_list } = ApiConfig;
    const url = baseAuth + refer_list;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  /** Paid referral commissions for the logged-in sponsor. */
  getReferralCommissionHistory: async ({ page = 1, limit = 20, market } = {}) => {
    const token = localStorage.getItem('token');
    const { baseAuth, referral_commission_history } = ApiConfig;
    const safeLimit = Math.min(100, Math.max(1, Number(limit) || 20));
    const safePage = Math.max(1, Number(page) || 1);
    const params = new URLSearchParams({
      page: String(safePage),
      limit: String(safeLimit),
    });
    if (market === "spot" || market === "futures") params.set("market", market);
    const url = `${baseAuth}${referral_commission_history}?${params.toString()}`;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  getLatestNews: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, getLatestNews } = ApiConfig;
    const url = baseAuth + getLatestNews;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  totalReferBalance: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, referral_balance } = ApiConfig;
    const url = baseAuth + referral_balance;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  getjoiningbalance: async () => {
    const token = localStorage.getItem('token');
    const { baseAuth, joining_balance } = ApiConfig;
    const url = baseAuth + joining_balance;
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);
  },

  getCoinDetails: async (currency_id) => {
    const token = localStorage.getItem("token");
    const { baseExchange, coin_details } = ApiConfig;
    const url = baseExchange + coin_details;
    const params = {
      currency_id
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  cancelWithdrawal: async (_id, amount, currency_id, fee) => {
    const token = localStorage.getItem("token");
    const { baseWallet, user_cancel_withdrawal } = ApiConfig;
    const url = baseWallet + user_cancel_withdrawal;
    const params = {
      _id, amount, currency_id, fee
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  /**
   * `POST /v1/wallet/user_cancel_withdrawal` — minimal body: `_id`, `currency_id` only.
   */
  cancelWithdrawalByIds: async (_id, currency_id) => {
    const token = localStorage.getItem("token");
    const { baseWallet, user_cancel_withdrawal } = ApiConfig;
    const url = baseWallet + user_cancel_withdrawal;
    const params = { _id, currency_id };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  /**
   * `POST /v1/wallet/verify-withdrawal` — no Bearer (per route); for callbacks / admin tools.
   */
  postVerifyWithdrawal: async (body) => {
    const { baseWallet, verifyWithdrawal } = ApiConfig;
    const url = baseWallet + verifyWithdrawal;
    const headers = { "Content-Type": "application/json" };
    return ApiCallPost(url, body, headers);
  },


  verifyDeposit: async (status, chain, currency_id) => {
    const token = localStorage.getItem('token');
    const { baseWallet, verify_deposit } = ApiConfig;
    const url = baseWallet + verify_deposit;
    const params = {
      status, chain, currency_id
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  transfer_funds: async (chain, currency) => {
    const token = localStorage.getItem('token');
    const { baseWallet, transfer_funds } = ApiConfig;
    const url = baseWallet + transfer_funds;
    const params = {
      chain, currency
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  closePosition: async (positionId) => {
    const token = localStorage.getItem('token');
    const { baseFutures, closePosition } = ApiConfig;
    const url = baseFutures + closePosition;
    const params = {
      positionId
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },
  cancelFutureOrder: async (orderId) => {
    const token = localStorage.getItem('token');
    const { baseFutures, cancel } = ApiConfig;
    const url = baseFutures + cancel;
    const params = {
      orderId
    };
    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  placeFutureOrder: async (baseCurrency, quoteCurrency, marketType, side, quantity, price, leverage, takeProfit, stopLoss, isTpSl) => {
    const token = localStorage.getItem('token');
    const { baseFutures, order } = ApiConfig;
    const url = baseFutures + order;
    const params = {
      baseCurrency: baseCurrency,
      quoteCurrency: quoteCurrency,
      marketType: marketType,
      side: side,
      quantity: quantity,
      leverage: leverage,
    };

    if (marketType === "LIMIT") {
      params.price = price
    };

    if (isTpSl) {
      if (takeProfit > 0) {
        params.takeProfit = takeProfit
      };
      if (stopLoss < 0) {
        params.stopLoss = stopLoss
      };
    };


    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  placeReverseFutureOrder: async (baseCurrency, quoteCurrency, side, quantity, leverage) => {
    const token = localStorage.getItem('token');
    const { baseFutures, order } = ApiConfig;
    const url = baseFutures + order;
    const params = {
      baseCurrency: baseCurrency,
      quoteCurrency: quoteCurrency,
      marketType: "MARKET",
      side: side,
      quantity: quantity,
      leverage: leverage,
    };
    console.log("🚀 ~ params:", params)


    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallPost(url, params, headers);
  },

  submitTicket: async (formData) => {
    const { baseSupport, submitTicket } = ApiConfig;
    const url = baseSupport + submitTicket;
    let raw = null;
    try {
      raw = localStorage.getItem("token") || sessionStorage.getItem("token");
    } catch {
      raw = null;
    }
    const token =
      raw && String(raw).trim() !== "" ? (String(raw).startsWith("Bearer ") ? raw : `Bearer ${raw}`) : null;
    const headers = {};
    if (token) headers.Authorization = token;
    return ApiCallPost(url, formData, headers);
  },

  getUserTickets: async () => {
    const { baseSupport, getUserTickets } = ApiConfig;
    const url = baseSupport + getUserTickets;
    let raw = null;
    try {
      raw = localStorage.getItem("token") || sessionStorage.getItem("token");
    } catch {
      raw = null;
    }
    const token =
      raw && String(raw).trim() !== "" ? (String(raw).startsWith("Bearer ") ? raw : `Bearer ${raw}`) : null;
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = token;
    return ApiCallGet(url, headers);
  },

  getTicketCategories: async () => {
    const { baseSupport, getTicketCategories } = ApiConfig;
    const url = baseSupport + getTicketCategories;
    const headers = {
      "Content-Type": "application/json"
    };

    return ApiCallGet(url, headers);
  },

  replyTicket: async (messagerply, id) => {
    const token = localStorage.getItem("token");
    const { baseSupport, replyTicket } = ApiConfig;
    const url = baseSupport + replyTicket;
    const params = {
      replyBy: 1,
      query: messagerply,
      ticket_id: id,
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  request_refund: async (order_id) => {
    const token = localStorage.getItem("token");
    const { baseP2p, request_refund } = ApiConfig;
    const url = baseP2p + request_refund;
    const params = { order_id }
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  getReferredUserData: async (code) => {
    const url = `https://cvtoken.us/stake/api/user-data/${code}`
    const headers = {
      "Content-Type": "application/json",
    };
    return ApiCallGet(url, headers);
  },



  // P2P Ends here........


  // Options Routes


  optionPairs: async () => {
    const token = localStorage.getItem('token');
    const { baseOptions, optionPairs } = ApiConfig;
    const url = baseOptions + optionPairs;

    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);

  },

  contractDates: async (underlying) => {
    const token = localStorage.getItem('token');
    const { baseOptions, contractDates } = ApiConfig;
    const url = baseOptions + contractDates + `?underlying=${underlying}`;

    const headers = {
      'Content-Type': 'application/json',
      "Authorization": token,
    };
    return ApiCallGet(url, headers);

  },

  /** POST /v1/options/order — place limit/market order */
  placeOptionOrder: async (payload, idempotencyKey) => {
    const { baseOptions, placeOptionOrder } = ApiConfig;
    const url = `${baseOptions}${placeOptionOrder}`;
    const headers = bearerAuthHeaders();
    if (idempotencyKey) {
      headers["Idempotency-Key"] = String(idempotencyKey);
    }
    return ApiCallPost(url, payload, headers);
  },

  /** DELETE /v1/options/order/:id */
  cancelOptionOrder: async (orderId) => {
    const { baseOptions, cancelOptionOrder } = ApiConfig;
    const id = encodeURIComponent(String(orderId || "").trim());
    const url = `${baseOptions}${cancelOptionOrder}/${id}`;
    return ApiCallDelete(url, bearerAuthHeaders());
  },

  /** GET /v1/options/order/:id */
  getOptionOrder: async (orderId) => {
    const { baseOptions, optionsGetOrder } = ApiConfig;
    const id = encodeURIComponent(String(orderId || "").trim());
    const url = `${baseOptions}${optionsGetOrder}/${id}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  /** GET /v1/options/open-orders?symbol= */
  optionsOpenOrders: async (params = {}) => {
    const { baseOptions, optionsOpenOrders } = ApiConfig;
    const qs = new URLSearchParams();
    if (params.symbol) qs.set("symbol", params.symbol);
    const q = qs.toString();
    const url = `${baseOptions}${optionsOpenOrders}${q ? `?${q}` : ""}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  /** GET /v1/options/trades?symbol=&limit= */
  optionsLiveTrades: async (params = {}) => {
    const { baseOptions, optionsLiveTrades } = ApiConfig;
    const qs = new URLSearchParams();
    if (params.symbol) qs.set("symbol", params.symbol);
    const limit = params.limit != null ? Number(params.limit) : 50;
    if (Number.isFinite(limit) && limit > 0) qs.set("limit", String(Math.min(200, Math.floor(limit))));
    const q = qs.toString();
    const url = `${baseOptions}${optionsLiveTrades}${q ? `?${q}` : ""}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  /** GET /v1/options/positions — open options positions */
  optionsOpenPositions: async () => {
    const { baseOptions, optionsOpenPositions } = ApiConfig;
    const url = `${baseOptions}${optionsOpenPositions}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  /** GET /v1/options/wallet — options wallet (USDT) */
  optionsWallet: async () => {
    const { baseOptions, optionsWallet } = ApiConfig;
    const url = `${baseOptions}${optionsWallet}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  optionsPositionHistory: async (params = {}) => {
    const { baseOptions, optionsPositionHistory } = ApiConfig;
    const qs = new URLSearchParams();
    if (params.symbol) qs.set("symbol", params.symbol);
    if (params.side) qs.set("side", String(params.side).toUpperCase());
    if (params.status) qs.set("status", params.status);
    const from = params.from ?? params.start_date;
    const to = params.to ?? params.end_date;
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    qs.set("skip", String(params.skip ?? 0));
    const limit = params.limit != null ? Number(params.limit) : 50;
    qs.set("limit", String(Number.isFinite(limit) ? Math.min(200, Math.max(1, Math.floor(limit))) : 50));
    const q = qs.toString();
    const url = `${baseOptions}${optionsPositionHistory}${q ? `?${q}` : ""}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  optionsOrderHistory: async (params = {}) => {
    const { baseOptions, optionsOrderHistory } = ApiConfig;
    const qs = new URLSearchParams();
    if (params.symbol) qs.set("symbol", params.symbol);
    if (params.side) qs.set("side", String(params.side).toUpperCase());
    if (params.status) qs.set("status", params.status);
    const from = params.from ?? params.start_date;
    const to = params.to ?? params.end_date;
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    qs.set("skip", String(params.skip ?? 0));
    const limit = params.limit != null ? Number(params.limit) : 50;
    qs.set("limit", String(Number.isFinite(limit) ? Math.min(200, Math.max(1, Math.floor(limit))) : 50));
    const q = qs.toString();
    const url = `${baseOptions}${optionsOrderHistory}${q ? `?${q}` : ""}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  optionsTradeHistory: async (params = {}) => {
    const token = localStorage.getItem('token');
    const { baseOptions, optionsTradeHistory } = ApiConfig;
    const qs = new URLSearchParams();
    if (params.underlying) qs.set('underlying', params.underlying);
    if (params.symbol) qs.set('symbol', params.symbol);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    qs.set('skip', String(params.skip ?? 0));
    qs.set('limit', String(params.limit ?? 20));
    const url = `${baseOptions}${optionsTradeHistory}?${qs}`;
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallGet(url, headers);
  },

  optionsExerciseHistory: async (params = {}) => {
    const token = localStorage.getItem('token');
    const { baseOptions, optionsExerciseHistory } = ApiConfig;
    const qs = new URLSearchParams();
    if (params.underlying) qs.set('underlying', params.underlying);
    if (params.symbol) qs.set('symbol', params.symbol);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    qs.set('skip', String(params.skip ?? 0));
    qs.set('limit', String(params.limit ?? 20));
    const url = `${baseOptions}${optionsExerciseHistory}?${qs}`;
    const headers = { 'Content-Type': 'application/json', Authorization: token };
    return ApiCallGet(url, headers);
  },

  optionsTransactionHistory: async (params = {}) => {
    const { baseOptions, optionsTransactionHistory } = ApiConfig;
    const qs = new URLSearchParams();
    if (params.asset) qs.set("asset", params.asset);
    if (params.symbol) qs.set("symbol", params.symbol);
    if (params.type) qs.set("type", params.type);
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    const page = Math.max(1, parseInt(params.page, 10) || 1);
    const limit = Math.min(100, Math.max(1, parseInt(params.limit, 10) || 20));
    qs.set("page", String(page));
    qs.set("limit", String(limit));
    const url = `${baseOptions}${optionsTransactionHistory}?${qs}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  /** GET /v1/options/pnl/analysis — summary cards, range stats, daily series + chart data */
  optionsPnlAnalysis: async (params = {}) => {
    const { baseOptions, optionsPnlAnalysis } = ApiConfig;
    const qs = buildOptionsPnlAnalysisQuery(params);
    const url = `${baseOptions}${optionsPnlAnalysis}${qs.toString() ? `?${qs}` : ""}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  /** GET /v1/options/pnl/analysis/details — paginated daily rows (newest first) */
  optionsPnlAnalysisDetails: async (params = {}) => {
    const { baseOptions, optionsPnlAnalysisDetails } = ApiConfig;
    const qs = buildOptionsPnlAnalysisDetailsQuery(params);
    const url = `${baseOptions}${optionsPnlAnalysisDetails}?${qs}`;
    return ApiCallGet(url, bearerAuthHeaders());
  },


  // P2P Routes Starts Here
  getCurrency: async () => {
    const token = localStorage.getItem("token");
    const { baseP2p, getCurrency } = ApiConfig;
    const url = baseP2p + getCurrency;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallGet(url, headers);
  },

  getFiatCurrency: async () => {
    const token = localStorage.getItem("token");
    const { baseP2p, getFiatCurrency } = ApiConfig;
    const url = baseP2p + getFiatCurrency;
    console.log(url, 'urlurlurl');
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallGet(url, headers);
  },

  getAllPaymentMethods: async () => {
    const token = localStorage.getItem("token");
    const { baseP2p, getAllPaymentMethods } = ApiConfig;
    const url = baseP2p + getAllPaymentMethods;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallGet(url, headers);
  },

  p2pSellOrderForBuyer: async (params = {}) => {
    const token = localStorage.getItem("token");
    const { baseP2p, p2pSellOrderForBuyer } = ApiConfig;
    const queryParams = new URLSearchParams();
    if (params.fiat) queryParams.append('fiat', params.fiat);
    if (params.crypto) queryParams.append('crypto', params.crypto);
    if (params.paymentType && params.paymentType !== 'All payments') queryParams.append('paymentType', params.paymentType);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const url = baseP2p + p2pSellOrderForBuyer + (queryParams.toString() ? `?${queryParams.toString()}` : '');
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallGet(url, headers);
  },
  p2pBuyOrderForSeller: async (params = {}) => {
    const token = localStorage.getItem("token");
    const { baseP2p, p2pBuyOrderForSeller } = ApiConfig;
    const queryParams = new URLSearchParams();
    if (params.fiat) queryParams.append('fiat', params.fiat);
    if (params.crypto) queryParams.append('crypto', params.crypto);
    if (params.paymentType && params.paymentType !== 'All payments') queryParams.append('paymentType', params.paymentType);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const url = baseP2p + p2pBuyOrderForSeller + (queryParams.toString() ? `?${queryParams.toString()}` : '');
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallGet(url, headers);
  },

  getUserPaymentMethods: async () => {
    const token = localStorage.getItem("token");
    const { baseP2p, getUserPaymentMethods } = ApiConfig;
    const url = baseP2p + getUserPaymentMethods;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallGet(url, headers);
  },

  getPaymentMethodFields: async (id) => {
    const token = localStorage.getItem("token");
    const { baseP2p, getPaymentMethodFields } = ApiConfig;
    const url = baseP2p + getPaymentMethodFields + `?id=${id}`;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallGet(url, headers);
  },

  getPairPrice: async (crypto, fiat) => {
    const token = localStorage.getItem("token");
    const { baseP2p, getPairPrice } = ApiConfig;
    const url = baseP2p + getPairPrice + `?crypto=${crypto}&fiat=${fiat}`;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallGet(url, headers);
  },

  getUserAds: async (params = {}) => {
    const token = localStorage.getItem("token");
    const { baseP2p, getUserAds } = ApiConfig;
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.side) queryParams.append('side', params.side);
    if (params.status) queryParams.append('status', params.status);
    if (params.fiatCurrency) queryParams.append('fiatCurrency', params.fiatCurrency);
    if (params.qouteCurrency) queryParams.append('qouteCurrency', params.qouteCurrency);
    const url = baseP2p + getUserAds + (queryParams.toString() ? `?${queryParams.toString()}` : '');
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallGet(url, headers);
  },

  closeAd: async (adId, reason = '') => {
    const token = localStorage.getItem("token");
    const { baseP2p, closeAd } = ApiConfig;
    const url = baseP2p + closeAd;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    const payload = { adId };
    if (reason && reason.trim()) {
      payload.reason = reason.trim();
    }
    return ApiCallPost(url, payload, headers);
  },

  updateAdStatus: async (adId, isOnline) => {
    const token = localStorage.getItem("token");
    const { baseP2p, updateAdStatus } = ApiConfig;
    const url = baseP2p + updateAdStatus;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallPost(url, { adId, isOnline }, headers);
  },

  addUserPaymentMethod: async (formData) => {
    const token = localStorage.getItem("token");
    const { baseP2p, addUserPaymentMethod } = ApiConfig;
    const url = baseP2p + addUserPaymentMethod;
    const headers = {
      "Content-Type": "multipart/form-data",
      'Authorization': token
    };
    return ApiCallPost(url, formData, headers);
  },

  deleteUserPaymentMethod: async (paymentMethodId) => {
    const token = localStorage.getItem("token");
    const { baseP2p, deleteUserPaymentMethod } = ApiConfig;
    const url = baseP2p + deleteUserPaymentMethod;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallPost(url, { paymentMethodId }, headers);
  },

  createAd: async (formData) => {
    const token = localStorage.getItem("token");
    const { baseP2p, createAd } = ApiConfig;
    const url = baseP2p + createAd;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallPost(url, formData, headers);
  },
  buyFromAd: async (formData) => {
    const token = localStorage.getItem("token");
    const { baseP2p, buyFromAd } = ApiConfig;
    const url = baseP2p + buyFromAd;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallPost(url, formData, headers);
  },

  sellFromAd: async (formData) => {
    const token = localStorage.getItem("token");
    const { baseP2p, sellFromAd } = ApiConfig;
    const url = baseP2p + sellFromAd;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallPost(url, formData, headers);
  },

  getP2pOrderDetails: async (orderId) => {
    const token = localStorage.getItem("token");
    const { baseP2p, getP2pOrderDetails } = ApiConfig;
    const url = baseP2p + getP2pOrderDetails + `/${orderId}`;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallGet(url, headers);
  },

  getP2pOrders: async (params = {}) => {
    const token = localStorage.getItem("token");
    const { baseP2p, getP2pOrders } = ApiConfig;
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.side) queryParams.append('side', params.side);
    if (params.crypto) queryParams.append('crypto', params.crypto);
    if (params.fiat) queryParams.append('fiat', params.fiat);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const url = baseP2p + getP2pOrders + (queryParams.toString() ? `?${queryParams.toString()}` : '');
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallGet(url, headers);
  },

  // ============ P2P ORDER ACTIONS ============

  // Buyer marks payment as completed (with optional payment proof)
  markPaymentCompleted: async (orderId, paymentProof = null) => {
    const token = localStorage.getItem("token");
    const { baseP2p, markPaymentCompleted } = ApiConfig;
    const url = baseP2p + markPaymentCompleted;

    const formData = new FormData();
    formData.append('orderId', orderId);
    if (paymentProof) {
      formData.append('paymentProof', paymentProof);
    }

    const headers = {
      'Authorization': token
      // Note: Don't set Content-Type for FormData, let browser set it with boundary
    };
    return ApiCallPost(url, formData, headers);
  },

  // Seller releases crypto after verifying payment
  releaseCrypto: async (orderId) => {
    const token = localStorage.getItem("token");
    const { baseP2p, releaseCrypto } = ApiConfig;
    const url = baseP2p + releaseCrypto;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallPost(url, { orderId }, headers);
  },

  // Buyer cancels order (only PENDING_PAYMENT status)
  cancelP2pOrder: async (orderId, cancelReason = '') => {
    const token = localStorage.getItem("token");
    const { baseP2p, cancelP2pOrder } = ApiConfig;
    const url = baseP2p + cancelP2pOrder;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallPost(url, { orderId, cancelReason }, headers);
  },

  // Buyer raises dispute (only after PAID status)
  buyerRaiseDispute: async (orderId, reason) => {
    const token = localStorage.getItem("token");
    const { baseP2p, buyerDispute } = ApiConfig;
    const url = baseP2p + buyerDispute;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallPost(url, { orderId, reason }, headers);
  },

  // Seller raises dispute (only after PAID status)
  sellerRaiseDispute: async (orderId, reason) => {
    const token = localStorage.getItem("token");
    const { baseP2p, sellerDispute } = ApiConfig;
    const url = baseP2p + sellerDispute;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallPost(url, { orderId, reason }, headers);
  },

  getP2pProfile: async (userId = null) => {
    const token = localStorage.getItem("token");
    const { baseP2p, getP2pProfile } = ApiConfig;
    let url = baseP2p + getP2pProfile;
    if (userId) {
      url += `?userId=${userId}`;
    }
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallGet(url, headers);
  },

  // ============ P2P CHAT APIs ============

  // Send text message
  sendChatMessage: async (orderId, message) => {
    const token = localStorage.getItem("token");
    const { baseP2p, sendChatMessage } = ApiConfig;
    const url = baseP2p + sendChatMessage;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallPost(url, { orderId, message }, headers);
  },

  // Upload chat image
  uploadChatImage: async (orderId, file) => {
    const token = localStorage.getItem("token");
    const { baseP2p, uploadChatImage } = ApiConfig;
    const url = baseP2p + uploadChatImage;

    const formData = new FormData();
    formData.append('orderId', orderId);
    formData.append('image', file);

    const headers = {
      'Authorization': token
    };
    return ApiCallPost(url, formData, headers);
  },

  // Get chat history
  getChatHistory: async (orderId, params = {}) => {
    const token = localStorage.getItem("token");
    const { baseP2p, getChatHistory } = ApiConfig;
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.before) queryParams.append('before', params.before);
    const url = baseP2p + getChatHistory + `/${orderId}` + (queryParams.toString() ? `?${queryParams.toString()}` : '');
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallGet(url, headers);
  },

  // Mark chat messages as read
  markChatAsRead: async (orderId) => {
    const token = localStorage.getItem("token");
    const { baseP2p, markChatRead } = ApiConfig;
    const url = baseP2p + markChatRead;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallPost(url, { orderId }, headers);
  },

  // Get unread message count
  getChatUnreadCount: async (orderId = null) => {
    const token = localStorage.getItem("token");
    const { baseP2p, getChatUnreadCount } = ApiConfig;
    let url = baseP2p + getChatUnreadCount;
    if (orderId) {
      url += `?orderId=${orderId}`;
    }
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallGet(url, headers);
  },

  // Delete chat message
  deleteChatMessage: async (messageId) => {
    const token = localStorage.getItem("token");
    const { baseP2p, deleteChatMessage } = ApiConfig;
    const url = baseP2p + deleteChatMessage;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': token
    };
    return ApiCallPost(url, { messageId }, headers);
  },

  // P2P ends here


  // ============================================================================
  // SECURITY & 2FA METHODS
  // ============================================================================

  /**
   * Send OTP for security verification (2FA setup, email change, phone change, etc.)
   * @param {string} target - Target type: 'email', 'mobile', 'new_email', 'new_mobile'
   * @param {string} purpose - Purpose of OTP (login, 2fa_setup, email_change, phone_change, etc.)
   * @param {string} value - Optional: New email/phone value when target is 'new_email' or 'new_mobile'
   */
  /**
   * Get user's security status and methods
   */
  getSecurityStatus: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityStatus } = ApiConfig;
    const url = baseSecurity + securityStatus;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * Get user's enabled security methods (email/mobile/totp/passkey).
   * GET /v1/security/get-security-methods-list
   */
  getSecurityMethodsList: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity } = ApiConfig;
    const url = baseSecurity + "get-security-methods-list";
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * POST /v1/security/unavailable-reset/start
   * Body: `{ methods: ["email"|"mobile"|"totp"|"passkey"], freeze_acknowledged: true }`
   */
  startUnavailableSecurityReset: async ({ methods, freeze_acknowledged = true } = {}) => {
    const { baseSecurity, startUnavailableSecurityReset: path } = ApiConfig;
    const url = baseSecurity + path;
    return ApiCallPost(
      url,
      { methods: Array.isArray(methods) ? methods : [], freeze_acknowledged: freeze_acknowledged === true },
      bearerAuthHeaders()
    );
  },

  /**
   * POST /v1/security/reset-unavailable-security-methods
   * Body: `{ methods, freeze_acknowledged: true, resetToken }`
   */
  resetUnavailableSecurityMethods: async ({ methods, freeze_acknowledged = true, resetToken = "" } = {}) => {
    const { baseSecurity, resetUnavailableSecurityMethods: path } = ApiConfig;
    const url = baseSecurity + path;
    return ApiCallPost(
      url,
      {
        methods: Array.isArray(methods) ? methods : [],
        freeze_acknowledged: freeze_acknowledged === true,
        resetToken: String(resetToken || ""),
      },
      bearerAuthHeaders()
    );
  },

  /**
   * GET /v1/security/wallet-total-btc — aggregated wallet value (BTC / USDT estimates).
   */
  getSecurityWalletTotalBtc: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityWalletTotalBtc: path } = ApiConfig;
    const url = baseSecurity + path;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * GET /v1/security/get-connected-accounts — `{ success, data: [{ type, email }] }`
   */
  getConnectedAccounts: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityGetConnectedAccounts: path } = ApiConfig;
    const url = baseSecurity + path;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * GET /v1/security/get-connected-account-count — `{ success, data: number }`
   */
  getConnectedAccountCount: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityGetConnectedAccountCount: path } = ApiConfig;
    const url = baseSecurity + path;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * POST /v1/security/connect-disconnect-account
   * Body: `{ action, account_type, type, code?, credential? }` — `account_type`: `"google"` | `"apple"` (linked OAuth row).
   */
  connectDisconnectAccount: async ({ action, type, code = "", credential, account_type, provider } = {}) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityConnectDisconnectAccount: path } = ApiConfig;
    const url = baseSecurity + path;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    const t = String(type ?? "").trim().toLowerCase();
    const act = String(action ?? "").trim();
    const body = {
      action: act,
      account_type: String(account_type ?? provider ?? "").trim().toLowerCase(),
    };
    // Unbind: JWT + account_type only (OTP skipped on backend). Bind still sends type/code.
    if (act !== "disconnected") {
      body.type = t;
      body.code = code == null ? "" : String(code);
      if (t === "passkey") {
        body.credential = credential == null ? "" : credential;
      }
    }
    return ApiCallPost(url, body, headers);
  },

  /**
   * POST /v1/security/send-otp-for-connect-disconnect-account — same payload as disable-account send-otp.
   * @param {"email"|"mobile"|"phone"} security_methods
   */
  sendOtpForConnectDisconnectAccount: async (security_methods) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityConnectDisconnectSendOtp: path } = ApiConfig;
    const url = baseSecurity + path;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, { security_methods }, headers);
  },

  /**
   * Get enabled methods for login 2-step verification
   */
  securityCheckTwoLogin2Step: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityCheckTwoLogin2Step: path } = ApiConfig;
    const url = baseSecurity + path;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /** Same endpoint as `securityCheckTwoLogin2Step` (legacy name). */
  checkTwoLogin2Step: async function () {
    return this.securityCheckTwoLogin2Step();
  },

  /**
   * Update login 2-step method status (enable/disable)
   * @param {string} security_methods - "phone" | "email" | "totp" | "passkey"
   * @param {string} code - verification code or passkey user id for passkey
   * @param {string} action - "enable" | "disable"
   */
  securityUpdateTwoLogin2Step: async (security_methods, code, action) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityUpdateTwoLogin2Step: path } = ApiConfig;
    const url = baseSecurity + path;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    const params = {
      security_methods,
      code,
      action,
    };
    return ApiCallPost(url, params, headers);
  },

  /**
   * Send OTP for two login 2-step update actions
   * @param {string} security_methods - "phone" | "email"
   */
  securitySendOtpForTwoLogin2Step: async (security_methods) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securitySendOtpForTwoLogin2Step: path } = ApiConfig;
    const url = baseSecurity + path;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, { security_methods }, headers);
  },

  /**
   * Get latest security log(s)
   */
  securityGetLogs: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityGetLogs } = ApiConfig;
    const url = baseSecurity + securityGetLogs;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  securityGetLoginLogs: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityGetLoginLogs } = ApiConfig;
    const url = baseSecurity + securityGetLoginLogs;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  securityGetSecurityLogs: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityGetSecurityLogs } = ApiConfig;
    const url = baseSecurity + securityGetSecurityLogs;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * Add or update fund password
   * @param {string} fund_password
   * @param {string} confirm_fund_password
   * @param {string} code - OTP/TOTP/passkey user id based on type
   * @param {string} type - "totp" | "email" | "phone" | "passkey"
   */
  securityAddFundPassword: async (fund_password, confirm_fund_password, code, type) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityAddFundPassword } = ApiConfig;
    const url = baseSecurity + securityAddFundPassword;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    const params = { fund_password, confirm_fund_password, code, type };
    return ApiCallPost(url, params, headers);
  },

  securityGetFundPasswordStatus: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityGetFundPasswordStatus } = ApiConfig;
    const url = baseSecurity + securityGetFundPasswordStatus;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },


  /**
   * Send OTP for enabling login 2-step (email and/or phone). `security_methods` can be "email", "phone", or both.
   * @param {string|string[]} securityMethods - e.g. "email", "phone", or ["email","phone"]
   */
  sendOtpForTwoLogin2Step: async (securityMethods) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, sendOtpForTwoLogin2Step: path } = ApiConfig;
    const url = baseSecurity + path;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    const params = { security_methods: securityMethods };
    return ApiCallPost(url, params, headers);
  },

  /**
   * Send email/SMS OTP for disable-account verification.
   * POST /v1/security/disable-account/send-otp — body `{ security_methods: "email" | "mobile" }`.
   * @param {"email"|"mobile"} security_methods
   */
  securityDisableAccountSendOtp: async (security_methods) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityDisableAccountSendOtp: path } = ApiConfig;
    const url = baseSecurity + path;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, { security_methods }, headers);
  },

  /**
   * Complete disable-account (`router.post('/disable-account', token_verification, …)`).
   * POST /v1/security/disable-account
   * Body: `{ type: "email"|"phone"|"totp", code: string, credential: "" }` or `{ type: "passkey", code: "", credential }`.
   */
  securityDisableAccount: async (body) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityDisableAccount: disableAccountPath } = ApiConfig;
    const url = baseSecurity + disableAccountPath;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, body, headers);
  },

  /**
   * Send email/SMS OTP for close-account verification.
   * POST /v1/security/closed-account/send-otp — body `{ security_methods: "email" | "mobile" | "phone" }` (same as disable).
   * @param {"email"|"mobile"|"phone"} security_methods
   */
  securityClosedAccountSendOtp: async (security_methods) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityClosedAccountSendOtp: path } = ApiConfig;
    const url = baseSecurity + path;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, { security_methods }, headers);
  },

  /**
   * Complete close-account — same body contract as `securityDisableAccount`.
   * POST /v1/security/closed-account
   */
  securityClosedAccount: async (body) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityClosedAccount: closedAccountPath } = ApiConfig;
    const url = baseSecurity + closedAccountPath;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, body, headers);
  },

  securitySendOtp: async (target, purpose, value = null) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securitySendOtp } = ApiConfig;
    const url = baseSecurity + securitySendOtp;
    const params = { target, purpose };
    if (value) {
      params.value = value;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  /**
   * Verify OTP for security actions
   * @param {string} target - Target type: 'email', 'mobile', 'new_email', 'new_mobile'
   * @param {string} otp - 6-digit OTP code
   * @param {string} purpose - Purpose of OTP (e.g., '2fa_verification', 'change_email')
   * @param {string} identifier - Optional: New email/phone value when target is 'new_email' or 'new_mobile'
   */
  securityVerifyOtp: async (target, otp, purpose, identifier = null) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityVerifyOtp } = ApiConfig;
    const url = baseSecurity + securityVerifyOtp;
    const params = { target, otp, purpose };
    if (identifier) {
      params.identifier = identifier;
    }
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  /**
   * Get user's security status
   */
  securityGetStatus: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityStatus } = ApiConfig;
    const url = baseSecurity + securityStatus;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * Change login password — POST /v1/security/change-password
   * Body: { oldPassword, newPassword, confirmNewPassword }
   * Caller must have already passed `verifyAllSecurityMethods` for the user's chosen method.
   * @param {{ oldPassword: string, newPassword: string, confirmNewPassword: string }} payload
   */
  securityChangeLoginPassword: async ({ oldPassword, newPassword, confirmNewPassword } = {}) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityChangePassword } = ApiConfig;
    const url = baseSecurity + securityChangePassword;
    const params = {
      oldPassword: String(oldPassword ?? ""),
      newPassword: String(newPassword ?? ""),
      confirmNewPassword: String(confirmNewPassword ?? ""),
    };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  /**
   * Send OTP for reset-password — POST /v1/security/reset-password/send-otp
   * Body: `{ method: "email" | "mobile" }`
   * @param {"email"|"mobile"} method
   */
  securityResetPasswordSendOtp: async (method) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityResetPasswordSendOtp } = ApiConfig;
    const url = baseSecurity + securityResetPasswordSendOtp;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, { method }, headers);
  },

  /**
   * Verify reset-password OTP — POST /v1/security/reset-password/verify-otp
   * Body: `{ type: "email" | "mobile", code: string }`
   * @param {"email"|"mobile"} type
   * @param {string} code
   */
  securityResetPasswordVerifyOtp: async (type, code) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityResetPasswordVerifyOtp } = ApiConfig;
    const url = baseSecurity + securityResetPasswordVerifyOtp;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, { type, code: String(code ?? "") }, headers);
  },

  /**
   * Complete reset-password — POST /v1/security/reset-password
   * Body: `{ verificationToken, newPassword, confirmNewPassword }`
   */
  securityResetPasswordComplete: async ({ verificationToken, newPassword, confirmNewPassword } = {}) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityResetPassword } = ApiConfig;
    const url = baseSecurity + securityResetPassword;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(
      url,
      {
        verificationToken: String(verificationToken ?? ""),
        newPassword: String(newPassword ?? ""),
        confirmNewPassword: String(confirmNewPassword ?? ""),
      },
      headers
    );
  },

  /**
   * Unified security verification — POST /v1/security/verify-all-security-methods
   * Body: { type, code, credential, purpose? }
   *   - purpose "unavailable_reset": remaining methods for unavailable reset (progress + resetToken).
   *   - For "passkey": pass the WebAuthn `credential` returned by `startAuthentication()`; `code` stays "".
   *   - For "totp" / "email" / "mobile": pass the 6-digit `code`; `credential` stays "".
   * @param {{ type: string, code?: string, credential?: object|string, purpose?: string }} params
   */
  verifyAllSecurityMethods: async ({ type, code = "", credential = "", purpose = "" } = {}) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityVerifyAllMethods } = ApiConfig;
    const url = baseSecurity + securityVerifyAllMethods;
    const params = {
      type,
      code: code || "",
      credential: credential == null ? "" : credential,
    };
    if (purpose) params.purpose = purpose;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  /**
   * POST /v1/security/send-otp-for-email-or-mobile
   * Body: `{ security_methods: "email" | "mobile" }`
   */
  sendOtpForEmailOrMobile: async (security_methods) => {
    const { baseSecurity, sendOtpForEmailOrMobile: path } = ApiConfig;
    const url = baseSecurity + path;
    const channel = security_methods === "mobile" || security_methods === "phone" ? "mobile" : "email";
    return ApiCallPost(url, { security_methods: channel }, bearerAuthHeaders());
  },

  /**
   * Verify TOTP code for security actions
   * @param {string} code - 6-digit TOTP code from authenticator app
   * @param {string} purpose - Purpose of verification (e.g., 'add_passkey', 'change_email')
   */
  securityVerifyTotp: async (code, purpose) => {
    const token = localStorage.getItem("token");
    const { baseSecurity } = ApiConfig;
    const url = baseSecurity + "verify-totp";
    const params = { code: String(code), purpose };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  /**
   * Initiate 2FA setup - generates QR code and secret
   */
  security2faSetup: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, security2faSetup: setupEndpoint } = ApiConfig;
    const url = baseSecurity + setupEndpoint;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, {}, headers);
  },

  /**
   * Confirm 2FA setup - verifies TOTP code and activates 2FA
   * @param {string} code - 6-digit TOTP code from authenticator app
   */
  security2faConfirm: async (code) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, security2faConfirm: confirmEndpoint } = ApiConfig;
    const url = baseSecurity + confirmEndpoint;
    const params = { code };
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  /**
   * Disable 2FA
   * @param {string} authenticatorCode - The 6-digit code from Google Authenticator
   * @param {string} otpCode - OTP code for email/mobile verification
   * @param {string} verifyMethod - 'email', 'mobile', or 'passkey'
   * @param {string} passkeyUserId - User ID from passkey verification
   */
  security2faDisable: async (authenticatorCode, otpCode = null, verifyMethod = null, passkeyUserId = null) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, security2faDisable: disableEndpoint } = ApiConfig;
    const url = baseSecurity + disableEndpoint;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    const data = {};
    if (authenticatorCode) data.code = authenticatorCode;
    if (otpCode) data.otpCode = otpCode;
    if (verifyMethod) data.verifyMethod = verifyMethod;
    if (passkeyUserId) data.passkeyUserId = passkeyUserId;
    return ApiCallPost(url, data, headers);
  },

  /**
   * Add mobile number to account
   * @param {object} data - { mobileNumber, countryCode, mobileOtp }
   */
  securityMobileAdd: async (data) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityMobileAdd: mobileAddEndpoint } = ApiConfig;
    const url = baseSecurity + mobileAddEndpoint;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, data, headers);
  },

  /**
   * Add Email (for users who signed up with phone)
   * @param {object} data - { email, tofaCode (optional), mobileOtp, emailOtp }
   */
  securityEmailAdd: async (data) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityEmailAdd: emailAddEndpoint } = ApiConfig;
    const url = baseSecurity + emailAddEndpoint;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, data, headers);
  },

  /**
   * Initiate Email Change - stores pending change and sends OTP to new email
   * @param {object} data - { newEmail, tofaCode (optional), currentEmailOtp }
   */
  securityEmailChangeInitiate: async (data) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityEmailChangeInitiate: endpoint } = ApiConfig;
    const url = baseSecurity + endpoint;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, data, headers);
  },

  /**
   * Complete Email Change - verifies new email OTP and updates email
   * @param {object} data - { newEmailOtp }
   */
  securityEmailChangeComplete: async (data) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityEmailChangeComplete: endpoint } = ApiConfig;
    const url = baseSecurity + endpoint;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, data, headers);
  },

  /**
   * Initiate Mobile Change - stores pending change and sends OTP to new mobile
   * @param {object} data - { newMobileNumber, newCountryCode, tofaCode (optional), currentMobileOtp }
   */
  securityMobileChangeInitiate: async (data) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityMobileChangeInitiate: endpoint } = ApiConfig;
    const url = baseSecurity + endpoint;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, data, headers);
  },

  /**
   * Complete Mobile Change - verifies new mobile OTP and updates mobile
   * @param {object} data - { newMobileOtp }
   */
  securityMobileChangeComplete: async (data) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, securityMobileChangeComplete: endpoint } = ApiConfig;
    const url = baseSecurity + endpoint;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, data, headers);
  },

  // ============================================================================
  // ANTI-PHISHING CODE METHODS
  // ============================================================================

  /**
   * Get anti-phishing status and available verification methods
   */
  antiPhishingGetStatus: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, antiPhishingStatus } = ApiConfig;
    const url = baseSecurity + antiPhishingStatus;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * Send OTP for anti-phishing verification (email or mobile)
   * @param {string} target - 'email' or 'mobile'
   */
  antiPhishingSendOtp: async (target) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, antiPhishingSendOtp } = ApiConfig;
    const url = baseSecurity + antiPhishingSendOtp;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, { target }, headers);
  },

  /**
   * Add anti-phishing code
   * @param {object} params - { antiPhishingCode, verifyMethod, code?, passkeyUserId? }
   */
  antiPhishingAdd: async (params) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, antiPhishingAdd } = ApiConfig;
    const url = baseSecurity + antiPhishingAdd;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  /**
   * Remove anti-phishing code
   * @param {object} params - { verifyMethod, code?, passkeyUserId? }
   */
  antiPhishingRemove: async (params) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, antiPhishingRemove } = ApiConfig;
    const url = baseSecurity + antiPhishingRemove;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, params, headers);
  },

  // ============================================================================
  // PASSKEY (WebAuthn) METHODS
  // ============================================================================

  /**
   * Get passkey registration options (Step 1)
   * Returns challenge and options for WebAuthn registration
   */
  passkeyGetRegistrationOptions: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, passkeyRegisterOptions } = ApiConfig;
    const url = baseSecurity + passkeyRegisterOptions;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, {}, headers);
  },

  /**
   * Verify passkey registration (Step 2)
   * @param {object} credential - WebAuthn credential response
   * @param {string} name - User-friendly name for the passkey
   */
  passkeyVerifyRegistration: async (credential, name = null) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, passkeyRegisterVerify } = ApiConfig;
    const url = baseSecurity + passkeyRegisterVerify;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, { credential, name }, headers);
  },

  /**
   * Get list of user's passkeys
   */
  passkeyGetList: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, passkeyList } = ApiConfig;
    const url = baseSecurity + passkeyList;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * Rename a passkey
   * @param {string} passkeyId - ID of the passkey
   * @param {string} name - New name for the passkey
   */
  passkeyRename: async (passkeyId, name) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, passkeyRename: endpoint } = ApiConfig;
    const url = baseSecurity + endpoint;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, { passkeyId, name }, headers);
  },

  /**
   * Delete a passkey
   * @param {string} passkeyId - ID of the passkey
   * @param {string} verifyMethod - 'totp', 'email', or 'mobile'
   * @param {string} code - Verification code
   */
  passkeyDelete: async (passkeyId, verifyMethod, code, passkeyUserId = null) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, passkeyDelete: endpoint } = ApiConfig;
    const url = baseSecurity + endpoint;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    const data = { passkeyId, verifyMethod, code };
    if (passkeyUserId) {
      data.passkeyUserId = passkeyUserId;
    }
    return ApiCallPost(url, data, headers);
  },

  /**
   * Get passkey authentication options (for login)
   * @param {string} signId - Email or phone number
   */
  passkeyGetAuthOptions: async (signId) => {
    const { baseSecurity, passkeyAuthOptions } = ApiConfig;
    const url = baseSecurity + passkeyAuthOptions;
    return ApiCallPost(url, { signId }, deviceAuthHeaders());
  },

  /**
   * Authenticated step-up options (Security settings — does not log you in).
   */
  passkeyGetStepUpOptions: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, passkeyStepUpOptions } = ApiConfig;
    const url = baseSecurity + passkeyStepUpOptions;
    return ApiCallPost(url, {}, {
      "Content-Type": "application/json",
      Authorization: token,
    });
  },

  /**
   * Authenticated step-up verify — WebAuthn only, no new session tokens.
   * @param {object} credential - WebAuthn credential response
   */
  passkeyVerifyStepUp: async (credential) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, passkeyStepUpVerify } = ApiConfig;
    const url = baseSecurity + passkeyStepUpVerify;
    return ApiCallPost(url, { credential }, {
      "Content-Type": "application/json",
      Authorization: token,
    });
  },

  /**
   * Verify passkey authentication (for login)
   * @param {string} signId - Email or phone number
   * @param {object} credential - WebAuthn credential response
   * @param {object} [challengeOpts] - { challenge_id, tempToken }
   */
  passkeyVerifyAuth: async (signId, credential, challengeOpts = {}) => {
    const { baseSecurity, passkeyAuthVerify } = ApiConfig;
    const url = baseSecurity + passkeyAuthVerify;
    const body = { signId, credential };
    if (challengeOpts.challenge_id) body.challenge_id = challengeOpts.challenge_id;
    if (challengeOpts.tempToken) body.tempToken = challengeOpts.tempToken;
    return ApiCallPost(url, body, deviceAuthHeaders());
  },

  /**
   * Complete login after passkey verification
   * @param {string} signId - Email or phone number
   * @param {object} verificationData - Data from passkey verification
   * @param {object} [challengeOpts] - { challenge_id, tempToken }
   */
  completePasskeyLogin: async (signId, verificationData, challengeOpts = {}) => {
    const { baseSecurity } = ApiConfig;
    const url = baseSecurity + "passkey/login/complete";
    const body = { signId, ...verificationData };
    if (challengeOpts.challenge_id) body.challenge_id = challengeOpts.challenge_id;
    if (challengeOpts.tempToken) body.tempToken = challengeOpts.tempToken;
    return ApiCallPost(url, body, deviceAuthHeaders());
  },

  /**
   * Get discoverable passkey authentication options (sign in with passkey - no email required)
   */
  passkeyDiscoverableAuthOptions: async () => {
    const { baseSecurity } = ApiConfig;
    const url = baseSecurity + "passkey/discoverable/options";
    return ApiCallPost(url, {}, deviceAuthHeaders());
  },

  /**
   * Verify discoverable passkey authentication (sign in with passkey - no email required)
   * @param {object} credential - WebAuthn credential response
   * @param {string} challenge - Challenge from the options response
   */
  passkeyDiscoverableVerify: async (credential, challenge) => {
    const { baseSecurity } = ApiConfig;
    const url = baseSecurity + "passkey/discoverable/verify";
    return ApiCallPost(url, { credential, challenge }, deviceAuthHeaders());
  },

  /**
   * Emergency contacts — GET /v1/security/get-emergency-contact-list
   */
  getEmergencyContactList: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, emergencyContactList } = ApiConfig;
    const url = baseSecurity + emergencyContactList;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * GET /v1/security/get-emergency-contact-count — `{ success, data: number }`
   */
  getEmergencyContactCount: async () => {
    const token = localStorage.getItem("token");
    const { baseSecurity, emergencyContactCount: path } = ApiConfig;
    const url = baseSecurity + path;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallGet(url, headers);
  },

  /**
   * Send email/SMS OTP for emergency-contact verification.
   * POST /v1/security/emergency-contact/send-otp — body `{ security_methods: "email" | "mobile" }`
   */
  sendEmergencyContactOtp: async (security_methods) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, emergencyContactSendOtp: path } = ApiConfig;
    const url = baseSecurity + path;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    return ApiCallPost(url, { security_methods }, headers);
  },

  /**
   * POST /v1/security/add-emergency-contact
   * Body: { fullName, emailId, mobileNumber, type: "email"|"phone"|"totp"|"passkey", code?, credential? }
   */
  addEmergencyContact: async ({
    fullName,
    emailId,
    mobileNumber,
    type,
    code = "",
    credential,
  } = {}) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, emergencyContactAdd } = ApiConfig;
    const url = baseSecurity + emergencyContactAdd;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    const t = String(type ?? "").trim();
    const body = {
      fullName: String(fullName ?? "").trim(),
      emailId: String(emailId ?? "").trim(),
      mobileNumber: String(mobileNumber ?? "").trim(),
      type: t,
      code: code == null ? "" : String(code),
    };
    if (t === "passkey") {
      body.credential = credential == null ? "" : credential;
    }
    return ApiCallPost(url, body, headers);
  },

  /**
   * POST /v1/security/edit-emergency-contact
   * Same verification contract as add when backend expects it.
   */
  editEmergencyContact: async ({
    contactId,
    fullName,
    emailId,
    mobileNumber,
    type,
    code = "",
    credential,
  } = {}) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, emergencyContactEdit } = ApiConfig;
    const url = baseSecurity + emergencyContactEdit;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    const t = String(type ?? "").trim();
    const body = {
      contactId: String(contactId ?? ""),
      fullName: String(fullName ?? "").trim(),
      emailId: String(emailId ?? "").trim(),
      mobileNumber: String(mobileNumber ?? "").trim(),
      type: t,
      code: code == null ? "" : String(code),
    };
    if (t === "passkey") {
      body.credential = credential == null ? "" : credential;
    }
    return ApiCallPost(url, body, headers);
  },

  /**
   * POST /v1/security/delete-emergency-contact
   * Body: { contactId, type: "email"|"phone"|"totp"|"passkey", code?, credential? }
   */
  deleteEmergencyContact: async ({ contactId, type, code = "", credential } = {}) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, emergencyContactDelete } = ApiConfig;
    const url = baseSecurity + emergencyContactDelete;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    const t = String(type ?? "").trim();
    const body = {
      contactId: String(contactId ?? ""),
      type: t,
      code: code == null ? "" : String(code),
    };
    if (t === "passkey") {
      body.credential = credential == null ? "" : credential;
    }
    return ApiCallPost(url, body, headers);
  },

  /**
   * POST /v1/security/save-emergency-contact-message
   * Body: { contactId, message, type: "email"|"phone"|"totp"|"passkey", code?, credential? }
   */
  saveEmergencyContactMessage: async ({ contactId, message, type, code = "", credential } = {}) => {
    const token = localStorage.getItem("token");
    const { baseSecurity, emergencyContactSaveMessage } = ApiConfig;
    const url = baseSecurity + emergencyContactSaveMessage;
    const headers = {
      "Content-Type": "application/json",
      Authorization: token,
    };
    const t = String(type ?? "").trim();
    const body = {
      contactId: String(contactId ?? ""),
      message: String(message ?? ""),
      type: t,
      code: code == null ? "" : String(code),
    };
    if (t === "passkey") {
      body.credential = credential == null ? "" : credential;
    }
    return ApiCallPost(url, body, headers);
  },

  // ============================================================================
  // SESSION / DEVICE MANAGEMENT (access + refresh sessions)
  // ============================================================================

  listSessions: async () => {
    const url = `${ApiConfig.baseSecurity}sessions`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  listDevices: async () => {
    const url = `${ApiConfig.baseSecurity}devices`;
    return ApiCallGet(url, bearerAuthHeaders());
  },

  trustDevice: async (deviceId) => {
    const id = encodeURIComponent(String(deviceId || "").trim());
    const url = `${ApiConfig.baseSecurity}devices/${id}/trust`;
    return ApiCallPost(url, { device_id: deviceId }, bearerAuthHeaders());
  },

  untrustDevice: async (deviceId) => {
    const id = encodeURIComponent(String(deviceId || "").trim());
    const url = `${ApiConfig.baseSecurity}devices/${id}/untrust`;
    return ApiCallPost(url, { device_id: deviceId }, bearerAuthHeaders());
  },

  renameDevice: async (deviceId, deviceName) => {
    const id = encodeURIComponent(String(deviceId || "").trim());
    const url = `${ApiConfig.baseSecurity}devices/${id}/rename`;
    return ApiCallPost(url, { device_name: deviceName }, bearerAuthHeaders());
  },

  /** Soft-remove device + revoke its sessions. */
  removeDevice: async (deviceId) => {
    const id = encodeURIComponent(String(deviceId || "").trim());
    const url = `${ApiConfig.baseSecurity}devices/${id}/remove`;
    return ApiCallPost(url, { device_id: deviceId }, bearerAuthHeaders());
  },

  /** Bind device to current client IP. */
  bindDeviceIp: async (deviceId) => {
    const id = encodeURIComponent(String(deviceId || "").trim());
    const url = `${ApiConfig.baseSecurity}devices/${id}/bind-ip`;
    return ApiCallPost(url, { device_id: deviceId }, bearerAuthHeaders());
  },

  /** Clear device IP bind. */
  unbindDeviceIp: async (deviceId) => {
    const id = encodeURIComponent(String(deviceId || "").trim());
    const url = `${ApiConfig.baseSecurity}devices/${id}/unbind-ip`;
    return ApiCallPost(url, { device_id: deviceId }, bearerAuthHeaders());
  },

  /** Bind current session to client IP. */
  bindSessionIp: async (sessionId) => {
    const id = encodeURIComponent(String(sessionId || "").trim());
    const url = `${ApiConfig.baseSecurity}sessions/${id}/bind-ip`;
    return ApiCallPost(url, {}, bearerAuthHeaders());
  },

  /** Clear session IP bind. */
  unbindSessionIp: async (sessionId) => {
    const id = encodeURIComponent(String(sessionId || "").trim());
    const url = `${ApiConfig.baseSecurity}sessions/${id}/unbind-ip`;
    return ApiCallPost(url, {}, bearerAuthHeaders());
  },

  revokeSession: async (sessionId) => {
    const id = encodeURIComponent(String(sessionId || "").trim());
    const url = `${ApiConfig.baseSecurity}sessions/${id}/revoke`;
    return ApiCallPost(url, {}, bearerAuthHeaders());
  },

  revokeOtherSessions: async () => {
    const url = `${ApiConfig.baseSecurity}sessions/revoke-others`;
    return ApiCallPost(url, {}, bearerAuthHeaders());
  },

  /** Force-logout every active session. */
  revokeAllSessions: async () => {
    const url = `${ApiConfig.baseSecurity}sessions/revoke-all`;
    return ApiCallPost(url, {}, bearerAuthHeaders());
  },

  /**
   * End the current session on the server (immediate Redis kill).
   * Prefer sending refresh_token so the refresh family is cleared too.
   */
  logoutSession: async (refreshToken) => {
    const url = `${ApiConfig.baseSecurity}auth/logout`;
    const body = {};
    const rt = String(refreshToken || "").trim();
    if (rt) body.refresh_token = rt;
    return ApiCallPost(url, body, bearerAuthHeaders());
  },

  /**
   * Temp block: pass durationHours; permanent: permanent=true.
   */
  blockDevice: async (deviceId, opts = {}) => {
    const id = encodeURIComponent(String(deviceId || "").trim());
    const url = `${ApiConfig.baseSecurity}devices/${id}/block`;
    const body = { device_id: deviceId };
    if (opts.permanent) {
      body.permanent = true;
    } else if (opts.durationHours != null) {
      body.duration_hours = Number(opts.durationHours);
    } else {
      body.duration_hours = 24;
    }
    return ApiCallPost(url, body, bearerAuthHeaders());
  },

  /** Lift temp/permanent block. */
  unblockDevice: async (deviceId) => {
    const id = encodeURIComponent(String(deviceId || "").trim());
    const url = `${ApiConfig.baseSecurity}devices/${id}/unblock`;
    return ApiCallPost(url, { device_id: deviceId }, bearerAuthHeaders());
  },

  // ============================================================================
  // END OF SECURITY METHODS
  // ============================================================================

};

export default AuthService;
